import React, { useEffect, useMemo, useState, useLayoutEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import "./game.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getDvSourceImageUrl } from "../../functions/themeAssets";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";

function toTitleCase(str) {
  return str ? str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : "";
}

const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: 18, height: 18 }}>
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const StarIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const SpeakerOnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
    <polygon points="4 9 8 9 12 5 12 19 8 15 4 15 4 9" fill="currentColor" stroke="none" />
    <path d="M16.5 8.5a5 5 0 0 1 0 7" />
    <path d="M19 6a8.5 8.5 0 0 1 0 12" />
  </svg>
);

const SpeakerMutedIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
    <polygon points="4 9 8 9 12 5 12 19 8 15 4 15 4 9" fill="currentColor" stroke="none" />
    <line x1="16" y1="9" x2="21" y2="14" />
    <line x1="21" y1="9" x2="16" y2="14" />
  </svg>
);

const USER_DATA_KEY = "userData";

// GIF placement bounds per state (SVG coords, derived from each state's real
// projected boundary — see scripts/india-map generation notes in PR history).
const STATE_GIF_BOUNDS = {
  map_1: { x: 188.0, y: 402.0, width: 81.9, height: 72.4 },
  map_2: { x: 473.1, y: 174.5, width: 60.0, height: 31.1 },
  map_3: { x: 436.5, y: 202.8, width: 64.5, height: 42.3 },
  map_4: { x: 309.1, y: 219.6, width: 50.8, height: 35.8 },
  map_5: { x: 246.9, y: 291.5, width: 42.4, height: 69.8 },
  map_6: { x: 109.6, y: 435.3, width: 20.0, height: 20.0 },
  map_7: { x: 43.5, y: 272.4, width: 61.1, height: 50.8 },
  map_8: { x: 146.1, y: 148.6, width: 32.0, height: 36.3 },
  map_9: { x: 161.5, y: 95.9, width: 35.0, height: 31.8 },
  map_11: { x: 309.9, y: 259.6, width: 47.5, height: 37.4 },
  map_12: { x: 135.9, y: 420.5, width: 45.9, height: 75.9 },
  map_13: { x: 150.4, y: 519.3, width: 26.1, height: 49.8 },
  map_14: { x: 153.5, y: 248.6, width: 89.8, height: 64.2 },
  map_15: { x: 115.5, y: 327.4, width: 84.4, height: 71.1 },
  map_16: { x: 478.3, y: 246.4, width: 20.0, height: 20.4 },
  map_17: { x: 424.7, y: 230.4, width: 30.3, height: 20.0 },
  map_18: { x: 458.9, y: 271.1, width: 20.0, height: 28.4 },
  map_19: { x: 489.2, y: 219.7, width: 20.0, height: 20.4 },
  map_20: { x: 281.3, y: 315.3, width: 62.4, height: 52.6 },
  map_21: { x: 129.3, y: 116.8, width: 31.2, height: 33.5 },
  map_22: { x: 70.9, y: 179.8, width: 89.9, height: 79.0 },
  map_23: { x: 377.8, y: 189.4, width: 20.0, height: 20.0 },
  map_24: { x: 179.5, y: 502.5, width: 42.1, height: 60.7 },
  map_25: { x: 189.4, y: 373.6, width: 46.7, height: 45.2 },
  map_26: { x: 438.5, y: 266.5, width: 20.0, height: 20.0 },
  map_27: { x: 202.2, y: 176.4, width: 77.1, height: 72.3 },
  map_28: { x: 197.7, y: 132.2, width: 35.5, height: 30.5 },
  map_29: { x: 357.4, y: 242.3, width: 41.4, height: 62.2 },
};

// Approximate center of each state (% of SVG 570×600), used for hover labels.
const STATE_CENTERS = {
  map_1: { x: 40, y: 73 },
  map_2: { x: 88, y: 32 },
  map_3: { x: 82, y: 37 },
  map_4: { x: 59, y: 40 },
  map_5: { x: 47, y: 54 },
  map_6: { x: 21, y: 74 },
  map_7: { x: 13, y: 50 },
  map_8: { x: 28, y: 28 },
  map_9: { x: 31, y: 19 },
  map_11: { x: 59, y: 46 },
  map_12: { x: 28, y: 76 },
  map_13: { x: 29, y: 91 },
  map_14: { x: 35, y: 47 },
  map_15: { x: 28, y: 61 },
  map_16: { x: 86, y: 43 },
  map_17: { x: 77, y: 40 },
  map_18: { x: 82, y: 48 },
  map_19: { x: 88, y: 38 },
  map_20: { x: 55, y: 57 },
  map_21: { x: 25, y: 22 },
  map_22: { x: 20, y: 37 },
  map_23: { x: 68, y: 33 },
  map_24: { x: 35, y: 89 },
  map_25: { x: 37, y: 66 },
  map_26: { x: 79, y: 46 },
  map_27: { x: 42, y: 35 },
  map_28: { x: 38, y: 25 },
  map_29: { x: 66, y: 46 },
};

/**
 * Island territories aren't drawn as shapes on this simplified map — just
 * labeled by name (approximate real-world position) so players know they
 * exist. Not clickable/playable.
 */
const ISLAND_LABELS = [
  { id: "lakshadweep", x: 78, y: 500, lines: ["LAKSHADWEEP"] },
  { id: "andaman_nicobar", x: 532, y: 430, lines: ["ANDAMAN &", "NICOBAR"] },
];

/** Surrounding sea/ocean names — decorative labels, not clickable/playable. */
const SEA_LABELS = [
  { id: "arabian_sea", x: 42, y: 380, lines: ["ARABIAN", "SEA"] },
  { id: "bay_of_bengal", x: 455, y: 350, lines: ["BAY OF", "BENGAL"] },
  { id: "indian_ocean", x: 258, y: 588, lines: ["INDIAN OCEAN"] },
];

/** Decorative map regions (not playable — Jammu & Kashmir and Ladakh are Union Territories). */
const DECORATIVE_MAP_REGIONS = {
  map_10: { fill: "#00C1FF" },
  map_30: { fill: "#00C1FF" },
};

/** All SVG region ids in paint order (map_1 … map_29 states, map_30 Ladakh). */
const ALL_MAP_IDS = Array.from({ length: 30 }, (_, i) => `map_${i + 1}`);

// Map IDs to State Names (28 states — map_10 is decorative only, not a state)
const STATE_MAP = {
  map_1: "ANDHRA PRADESH",
  map_2: "ARUNACHAL PRADESH",
  map_3: "ASSAM",
  map_4: "BIHAR",
  map_5: "CHHATTISGARH",
  map_6: "GOA",
  map_7: "GUJARAT",
  map_8: "HARYANA",
  map_9: "HIMACHAL PRADESH",
  map_11: "JHARKHAND",
  map_12: "KARNATAKA",
  map_13: "KERALA",
  map_14: "MADHYA PRADESH",
  map_15: "MAHARASHTRA",
  map_16: "MANIPUR",
  map_17: "MEGHALAYA",
  map_18: "MIZORAM",
  map_19: "NAGALAND",
  map_20: "ODISHA",
  map_21: "PUNJAB",
  map_22: "RAJASTHAN",
  map_23: "SIKKIM",
  map_24: "TAMIL NADU",
  map_25: "TELANGANA",
  map_26: "TRIPURA",
  map_27: "UTTAR PRADESH",
  map_28: "UTTARAKHAND",
  map_29: "WEST BENGAL",
};

const PLAYABLE_STATES = Object.values(STATE_MAP);

function allPlayableStatesCompleted(completedList) {
  if (!Array.isArray(completedList) || completedList.length < PLAYABLE_STATES.length) {
    return false;
  }
  const done = new Set(completedList.map((s) => String(s).toUpperCase()));
  return PLAYABLE_STATES.every((state) => done.has(state));
}

// Completed-state fill per map region (from original map_config downColor)
const STATE_MAP_COLORS = {
  map_1:  "#00FF00",
  map_2:  "#9CA8B6",
  map_3:  "#00FFFF",
  map_4:  "#7FFFD4",
  map_5:  "#32CD32",
  map_6:  "#16F529",
  map_7:  "#66FF00",
  map_8:  "#66FF00",
  map_9:  "#FFA500",
  map_11: "#C04000",
  map_12: "#FF6700",
  map_13: "#FD1C03",
  map_14: "#7D0552",
  map_15: "#B21807",
  map_16: "#F52887",
  map_17: "#F433FF",
  map_18: "#440230",
  map_19: "#FF9800",
  map_20: "#00C1FF",
  map_21: "#FF9800",
  map_22: "#F433FF",
  map_23: "#8BC240",
  map_24: "#FF9800",
  map_25: "#55C1D7",
  map_26: "#F267A0",
  map_27: "#9D00FF",
  map_28: "#B8AFFF",
  map_29: "#9D00FF",
};

const DEFAULT_STATE_FILL = "#EBECED";

function readSessionUserData() {
  try {
    return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function formatTime(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}

/** Parse "MM:SS" (or "HH:MM:SS") back into total seconds for resuming the timer. */
function parseTimeToSeconds(value) {
  if (value == null) return 0;
  const parts = String(value).trim().split(":").map((n) => Number(n));
  if (parts.some((n) => Number.isNaN(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

/** Normalize completed-state values to an uppercase array. */

/** Normalize completed-state values to an uppercase array. */
function toStateArray(value) {
  if (Array.isArray(value)) return value.map((s) => String(s).toUpperCase());
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((s) => String(s).toUpperCase());
    } catch {
      /* not json */
    }
  }
  return [];
}

function normalizeQuestionsByState(value) {
  let source = value;
  if (typeof source === "string" && source.trim()) {
    try {
      source = JSON.parse(source);
    } catch {
      source = {};
    }
  }

  const grouped = {};
  const addQuestion = (question, index = 0) => {
    const stateName = String(question?.state || "").toUpperCase();
    if (!PLAYABLE_STATES.includes(stateName)) return;
    if (!grouped[stateName]) {
      grouped[stateName] = [];
    }
    grouped[stateName].push({
      id: question?.id || `${stateName}-${grouped[stateName].length + 1}`,
      sort_order: question?.sort_order || index + 1,
      question_title: question?.question_title || "",
      question: question?.question || "",
      image: question?.image || "",
      option_one: question?.option_one || "",
      option_two: question?.option_two || "",
      option_three: question?.option_three || "",
      correct_answer: question?.correct_answer || "",
      answer_id: question?.answer_id || "",
      did_you_know: question?.did_you_know || "",
      type_question: question?.type_question || "radioselect",
      state: stateName,
    });
  };

  if (Array.isArray(source)) {
    source.forEach((question, index) => addQuestion(question, index));
    return grouped;
  }

  if (source && typeof source === "object") {
    Object.entries(source).forEach(([stateName, questions]) => {
      if (!Array.isArray(questions)) return;
      questions.forEach((question, index) => addQuestion({
        ...question,
        state: question?.state || stateName,
        sort_order: question?.sort_order || index + 1,
      }, index));
    });
  }

  return grouped;
}

export default function GamePage() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);
  const storedUserData = useMemo(() => readSessionUserData(), []);

  const [questionsByState, setQuestionsByState] = useState({});
  const [completedStates, setCompletedStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [textAnswer, setTextAnswer] = useState("");
  const [loadingQuestions, setLoadingQuestions] = useState(true);
  const [showInstructions, setShowInstructions] = useState(true);
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [answerLog, setAnswerLog] = useState([]); // every submitted answer (incl. typed)
  const [stateGifs, setStateGifs] = useState({});
  const [activeGif, setActiveGif] = useState(null); // { mapId, stateName, gifUrl }
  const [gifKey, setGifKey] = useState(0); // force re-mount to restart animation
  const [hoveredState, setHoveredState] = useState(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [feedback, setFeedback] = useState(null); // { isCorrect, correctAnswer, didYouKnow }
  const [canProceed, setCanProceed] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");
  const [inlineNotice, setInlineNotice] = useState("");
  const [gameCompleted, setGameCompleted] = useState(false);
  const gameCompletedRef = useRef(false);
  const [confettiActive, setConfettiActive] = useState(false);
  const proceedTimerRef = useRef(null);

  // Keep the map card at least as tall as the question panel's own natural
  // height (which is never changed, and grows/shrinks with question content),
  // applied as a min-height so the map card stretches to match a taller
  // question panel but never gets squeezed shorter than its own content (map
  // + secret-message letters) needs — that would clip/scroll instead of just
  // growing. Only applied side-by-side (desktop/tablet); mobile stacks the
  // cards and sizes the map card to its own content instead.
  const questionSideRef = useRef(null);
  const [mapCardHeight, setMapCardHeight] = useState(null);
  useLayoutEffect(() => {
    // The question panel only exists in the DOM once loadingQuestions is false, so this
    // effect must re-run on that transition — not just once on mount — otherwise the
    // ref is still null the one time we'd try to attach the observer.
    if (loadingQuestions) return undefined;
    const el = questionSideRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const stackedQuery = window.matchMedia("(max-width: 992px)");

    const measure = () => {
      if (stackedQuery.matches) {
        setMapCardHeight(null);
        return;
      }
      const height = el.getBoundingClientRect().height;
      if (height) setMapCardHeight(Math.round(height));
    };

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    stackedQuery.addEventListener("change", measure);
    return () => {
      observer.disconnect();
      stackedQuery.removeEventListener("change", measure);
    };
  }, [loadingQuestions]);

  const rightPoints = Number(themeData?.points ?? 10) || 10;

  const resetQuestionUi = useCallback(() => {
    setAnswerSubmitted(false);
    setFeedback(null);
    setCanProceed(false);
    setValidationMsg("");
    setConfettiActive(false);
    if (proceedTimerRef.current) {
      clearTimeout(proceedTimerRef.current);
      proceedTimerRef.current = null;
    }
  }, []);

  const userId = storedUserData?.userId || storedUserData?.userid || storedUserData?.id || "";
  const organizationId = storedUserData?.organizationId || "admin";
  const sessionId = storedUserData?.sessionId || "admin";
  const gameRunId =
    storedUserData?.gameRunId ||
    storedUserData?.game_run_id ||
    storedUserData?.stageId ||
    storedUserData?.reportId ||
    null;

  // External report id (game-server). Seeded from the saved session if present.
  const [reportId, setReportId] = useState(
    storedUserData?.reportId || storedUserData?.report_id || null
  );

  const fetchSavedReportMeta = useCallback(async () => {
    if (!adminToken || !userId) return null;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/fetchReport`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json().catch(() => ({}));
      return data.success ? data.report || null : null;
    } catch (error) {
      console.error("Failed to fetch saved report:", error);
      return null;
    }
  }, [adminToken, userId]);

  /**
   * Push points + time to the external reports server (add or update).
   */
  const pushExternalReport = async (currentScore, currentTime) => {
    const role = storedUserData?.role || storedUserData?.source;
    if (role === "demobypass" || role === "DEMO") return reportId || null;
    if (!userId) return reportId || null;

    const timeFormatted = formatTime(currentTime);

    try {
      const payload = {
        sessionId,
        organizationId,
        userId,
        role,
        token: storedUserData?.token,
        gameId: storedUserData?.gameId,
        name: storedUserData?.name || "",
        points: currentScore,
        time: timeFormatted,
        reportId: reportId || undefined,
      };
      console.log("Sending external report payload:", payload);

      const response = await sendReport(payload);
      console.log("External report response:", response);
      const newReportId = extractReportIdFromResponse(response);

      if (newReportId && newReportId !== reportId) {
        setReportId(newReportId);
        try {
          const merged = { ...storedUserData, reportId: newReportId };
          sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(merged));
        } catch (_) {
          /* ignore */
        }
        return newReportId;
      }

      return newReportId || reportId || null;
    } catch (error) {
      console.error("Failed to send external report:", error);
      return reportId || null;
    }
  };

  const persistEscapeGameProgress = useCallback(async (
    states = completedStates,
    currentScore = score,
    currentTime = elapsed,
    answers = answerLog,
    linkedReportId = reportId
  ) => {
    const role = storedUserData?.role || storedUserData?.source || "";
    if (!adminToken || !userId || !gameRunId) return false;

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/escapeGameProgress`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          organizationId,
          sessionId,
          userId,
          gameRunId,
          elapsedSeconds: Math.max(0, Number(currentTime) || 0),
          gameId: storedUserData?.gameId,
          name: storedUserData?.name || "",
          role,
          employeeId:
            storedUserData?.employeeId ||
            storedUserData?.emp_id ||
            storedUserData?.empId ||
            "",
          reportToken: storedUserData?.token,
          round: storedUserData?.round,
          completedStates: states,
          totalScore: currentScore,
          answerLog: answers,
          reportId: linkedReportId || undefined,
        }),
      });
      return res.ok;
    } catch (error) {
      console.error("Failed to save escape game progress:", error);
      return false;
    }
  }, [
    adminToken,
    answerLog,
    completedStates,
    elapsed,
    gameRunId,
    organizationId,
    reportId,
    score,
    sessionId,
    storedUserData,
    userId,
  ]);

  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.matchMedia("(max-width: 992px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 992px)");
    const onChange = () => setIsMobileViewport(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Get dynamic background (mobile vs desktop asset)
  const backgroundImage = useMemo(() => {
    if (!themeData) return "";
    const bgKey = isMobileViewport
      ? (themeData.background_mob || themeData.mobile_view_image)
      : (themeData.background_desk || themeData.desktop_view_image);
    return bgKey ? getDvSourceImageUrl(bgKey) : "";
  }, [themeData, isMobileViewport]);

  // Desktop background asset specifically (used for admin-facing popups regardless of viewport).
  const desktopBackgroundImage = useMemo(() => {
    if (!themeData) return "";
    const bgKey = themeData.background_desk || themeData.desktop_view_image;
    return bgKey ? getDvSourceImageUrl(bgKey) : "";
  }, [themeData]);

  const themeVars = useMemo(
    () => ({
      "--theme-button-color": themeData?.button_color ?? "#667eea",
      "--theme-button-text-color":
        themeData?.button_Textcolor ?? themeData?.button_text_color ?? "#ffffff",
      "--theme-text-color": themeData?.textcolor ?? themeData?.text_color ?? "#1f2937",
      "--timer-bg": themeData?.timer_color ?? themeData?.button_color ?? "#22c55e",
      "--score-bg": themeData?.score_color ?? themeData?.points_color ?? "#f59e0b",
    }),
    [themeData]
  );

  const [mapBgImageUrl] = useState("");
  const [mapBgApplyQuestion] = useState(false);

  const [soundEnabled] = useState(false);
  const [soundUrl] = useState("");
  const [soundMuted] = useState(false);
  const soundRef = useRef(null);

  const toggleSound = () => {};

  const [secretEnabled] = useState(false);
  const [secretRevealed] = useState([]);

  const revealNextSecretLetter = useCallback(async () => null, []);

  useEffect(() => {
    if (!backgroundImage) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const bg = `center / cover no-repeat url("${backgroundImage}")`;
    root.style.background = bg;
    body.style.background = bg;
    root.style.minHeight = "100dvh";
    body.style.minHeight = "100dvh";
    return () => {
      root.style.removeProperty("background");
      root.style.removeProperty("min-height");
      body.style.removeProperty("background");
      body.style.removeProperty("min-height");
    };
  }, [backgroundImage]);

  useEffect(() => {
    if (!themeData) return;

    let cancelled = false;

    const initializeLocalState = async () => {
      const resumedStates = toStateArray(
        storedUserData?.completedStates || storedUserData?.current || storedUserData?.states
      );
      if (!cancelled && resumedStates.length > 0) {
        setCompletedStates(resumedStates);
        setShowInstructions(false);
      }

      const localAnswers = Array.isArray(storedUserData?.answerLog) ? storedUserData.answerLog : [];
      if (!cancelled && localAnswers.length > 0) {
        setAnswerLog(localAnswers);
      }

      const groupedQuestions = normalizeQuestionsByState(
        storedUserData?.questionsByState ||
        storedUserData?.questions ||
        themeData?.questionsByState ||
        themeData?.questions ||
        {}
      );
      if (!cancelled) {
        setQuestionsByState(groupedQuestions);
        setLoadingQuestions(false);
        if (Object.keys(groupedQuestions).length === 0) {
          setInlineNotice("No local questions available. Only fetchReport, sendReport, and escapeGameProgress are active on this page.");
        }
      }

      const savedReport = await fetchSavedReportMeta();
      if (cancelled) return;

      if (savedReport?.reportId && !reportId) {
        setReportId(savedReport.reportId);
        try {
          const merged = { ...readSessionUserData(), reportId: savedReport.reportId };
          sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(merged));
        } catch (_) {
          /* ignore */
        }
      }
      if (savedReport?.points != null && Number(savedReport.points) >= 0) {
        setScore(Number(savedReport.points) || 0);
      }
      if (savedReport?.time != null) {
        setElapsed(parseTimeToSeconds(savedReport.time));
      }

      setProgressLoaded(true);
    };

    initializeLocalState();
    return () => {
      cancelled = true;
    };
  }, [themeData, storedUserData, fetchSavedReportMeta, reportId]);

  useEffect(() => {
    gameCompletedRef.current = gameCompleted;
  }, [gameCompleted]);

  // Save progress using the only allowed gameplay persistence endpoint.
  const saveProgress = async (states, currentScore, currentTime, answers = answerLog, pushExternal = false) => {
    if (gameCompletedRef.current || !userId) return;

    try {
      const linkedReportId = await pushExternalReport(currentScore, currentTime);
      await persistEscapeGameProgress(states, currentScore, currentTime, answers, linkedReportId);
    } catch (error) {
      console.error("Failed to save progress:", error);
    }
  };


  useEffect(() => () => {
    if (proceedTimerRef.current) clearTimeout(proceedTimerRef.current);
  }, []);

  // Timer
  useEffect(() => {
    if (loadingQuestions) return;

    const timer = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [loadingQuestions]);

  // Periodic progress save (every 30 seconds)
  useEffect(() => {
    if (loadingQuestions || !progressLoaded || gameCompleted) return;

    const saveInterval = setInterval(() => {
      if (gameCompletedRef.current) return;
      if (completedStates.length > 0) {
        saveProgress(completedStates, score, elapsed, answerLog);
      }
    }, 30000); // Save every 30 seconds

    return () => clearInterval(saveInterval);
  }, [loadingQuestions, progressLoaded, gameCompleted, completedStates, score, elapsed, answerLog]);

  // Save progress on unmount (when user leaves the page) — skip after game is finished.
  useEffect(() => {
    return () => {
      if (gameCompletedRef.current) return;
      if (completedStates.length > 0 && progressLoaded) {
        saveProgress(completedStates, score, elapsed, answerLog);
      }
    };
  }, [completedStates, score, elapsed, progressLoaded, answerLog]);

  // Handle state click on map
  const handleStateClick = (mapId) => {
    if (gameCompleted) return;

    // Don't allow switching states while a question is currently active
    if (selectedState && currentQuestion) {
      setInlineNotice("Please finish the current question before selecting another state.");
      return;
    }

    const stateName = STATE_MAP[mapId];
    if (!stateName) return;

    const stateNameUpper = stateName.toUpperCase();

    if (completedStates.includes(stateNameUpper)) {
      setInlineNotice(`You have already completed ${stateNameUpper}.`);
      return;
    }

    const stateQuestionList = questionsByState[stateNameUpper];
    if (!stateQuestionList || stateQuestionList.length === 0) {
      setInlineNotice(`No questions available for ${stateNameUpper}.`);
      return;
    }

    // Resume at the first question not already answered for this state,
    // instead of always restarting from index 0 (which caused points to be
    // re-awarded for questions already answered on a previous visit).
    const nextIndex = stateQuestionList.findIndex(
      (q) => !answerLog.some((a) => a.state === stateNameUpper && a.questionId === q.id)
    );

    setInlineNotice("");
    resetQuestionUi();
    setSelectedState(stateNameUpper);
    setCurrentQuestionIndex(nextIndex === -1 ? 0 : nextIndex);
    setSelectedAnswer("");
    setTextAnswer("");
    setShowInstructions(false);

    // Show GIF clipped to state shape
    const gifUrl = stateGifs[stateNameUpper];
    if (gifUrl && STATE_GIF_BOUNDS[mapId]) {
      setActiveGif({ mapId, stateName: stateNameUpper, gifUrl });
      setGifKey((k) => k + 1);
    } else {
      setActiveGif(null);
    }
  };

  const currentQuestion = selectedState && questionsByState[selectedState]
    ? questionsByState[selectedState][currentQuestionIndex]
    : null;

  const advanceAfterFeedback = async (newScore, updatedLog) => {
    resetQuestionUi();
    const stateQuestions = questionsByState[selectedState];

    if (currentQuestionIndex < stateQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setSelectedAnswer("");
      setTextAnswer("");
      saveProgress(completedStates, newScore, elapsed, updatedLog);
      return;
    }

    const newCompletedStates = [...completedStates, selectedState];
    setCompletedStates(newCompletedStates);

    // One state just finished — uncover one more letter of the secret message.
    let revealResult = null;
    if (secretEnabled) {
      revealResult = await revealNextSecretLetter();
    }

    // Demo sessions get a single state, then are upsold to subscribe.
    const isDemoSession =
      storedUserData?.role === "demobypass" || storedUserData?.source === "DEMO";
    if (isDemoSession) {
      gameCompletedRef.current = true;
      setGameCompleted(true);
      setSelectedState(null);
      setActiveGif(null);
      Swal.fire({
        title: "Thank you for playing!",
        text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
        icon: "info",
        confirmButtonText: "View Plans",
        allowOutsideClick: false,
        allowEscapeKey: false,
        background: backgroundImage
          ? `center / cover no-repeat url("${backgroundImage}")`
          : undefined,
      }).then(() => {
        window.location.href = (process.env.REACT_APP_BASE_URL || "") + "/plans";
      });
      return;
    }

    const isFinal = allPlayableStatesCompleted(newCompletedStates);

    setSelectedState(null);
    setActiveGif(null);
    setShowInstructions(true);

    if (isFinal) {
      if (secretEnabled) {
        const revealedMessage = revealResult?.revealed
          ? revealResult.revealed.map((c) => c || "").join("")
          : "";
        setConfettiActive(true);
        Swal.fire({
          title: "Congratulations!",
          html: `
            <p>You managed to unveil the final message.</p>
            ${revealedMessage ? `<p style="font-weight:700;font-size:19px;margin-top:12px;">"${revealedMessage}"</p>` : ""}
          `,
          confirmButtonText: "OK",
          allowOutsideClick: false,
          allowEscapeKey: false,
          background: desktopBackgroundImage
            ? `center / cover no-repeat url("${desktopBackgroundImage}")`
            : undefined,
          color: themeVars["--theme-text-color"],
        }).then(() => {
          handleGameComplete(newCompletedStates, newScore, elapsed, updatedLog);
        });
      } else {
        handleGameComplete(newCompletedStates, newScore, elapsed, updatedLog);
      }
    } else {
      saveProgress(newCompletedStates, newScore, elapsed, updatedLog, true);
    }
  };

  const handleAction = () => {
    if (!currentQuestion) return;

    if (answerSubmitted) {
      if (!canProceed) return;
      advanceAfterFeedback(score, answerLog);
      return;
    }

    let userAnswer = "";
    if (currentQuestion.type_question === "typetext") {
      userAnswer = textAnswer.trim();
      if (!userAnswer) {
        setValidationMsg("Please type your answer before submitting.");
        return;
      }
    } else {
      userAnswer = selectedAnswer;
      if (!userAnswer) {
        setValidationMsg("Please select an option before submitting.");
        return;
      }
    }

    setValidationMsg("");
    const isCorrect = userAnswer.toLowerCase() === currentQuestion.correct_answer.toLowerCase();

    // Guard against re-awarding points if this question was already answered
    // on a previous visit to this state (e.g. after switching away and back).
    const alreadyAnswered = answerLog.some(
      (a) => a.state === selectedState && a.questionId === currentQuestion.id
    );

    const answerEntry = {
      state: selectedState,
      questionId: currentQuestion.id,
      question: currentQuestion.question,
      type: currentQuestion.type_question,
      answer: userAnswer,
      correct_answer: currentQuestion.correct_answer,
      isCorrect,
      time: formatTime(elapsed),
    };
    const updatedLog = alreadyAnswered ? answerLog : [...answerLog, answerEntry];
    if (!alreadyAnswered) setAnswerLog(updatedLog);

    const newScore = isCorrect && !alreadyAnswered ? score + rightPoints : score;
    if (isCorrect && !alreadyAnswered) setScore(newScore);

    setAnswerSubmitted(true);
    setFeedback({
      isCorrect,
      correctAnswer: currentQuestion.correct_answer,
      didYouKnow: currentQuestion.did_you_know || "",
    });

    setCanProceed(true);
    if (isCorrect) {
      setConfettiActive(true);
    }
  };

  const handleGameComplete = async (
    finalStates = completedStates,
    finalScore = score,
    finalTime = elapsed,
    finalAnswers = answerLog
  ) => {
    if (gameCompleted || gameCompletedRef.current) return;
    gameCompletedRef.current = true;
    setGameCompleted(true);
    // alert("stage1 complete");
    await saveProgress(finalStates, finalScore, finalTime, finalAnswers, true);

    try {
      const merged = { ...storedUserData, gameover: 1 };
      sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(merged));
    } catch (_) {
      /* ignore */
    }

    navigate("/leaderboard", { replace: true });
  };

  // Resume edge case: all 28 states saved but status not yet finalized → go to leaderboard.
  useEffect(() => {
    if (loadingQuestions || !progressLoaded || gameCompleted) return;
    if (!allPlayableStatesCompleted(completedStates)) return;
    handleGameComplete(completedStates, score, elapsed, answerLog);
  }, [
    loadingQuestions,
    progressLoaded,
    gameCompleted,
    completedStates,
    score,
    elapsed,
    answerLog,
  ]);

  // Get fill color for each state
  const getStateFill = (mapId) => {
    if (DECORATIVE_MAP_REGIONS[mapId]) {
      return DECORATIVE_MAP_REGIONS[mapId].fill;
    }
    const stateName = STATE_MAP[mapId];
    if (!stateName) return DEFAULT_STATE_FILL;
    if (completedStates.includes(stateName.toUpperCase())) {
      return STATE_MAP_COLORS[mapId] ?? DEFAULT_STATE_FILL;
    }
    return DEFAULT_STATE_FILL;
  };

  // Parse admin rules from themeData — handles string, array, or other types
  const adminRules = useMemo(() => {
    const val = themeData?.rules || themeData?.themeDescription;
    if (!val) return null;
    if (Array.isArray(val)) {
      const arr = val.map((v) => String(v).trim()).filter(Boolean);
      return arr.length ? arr : null;
    }
    if (typeof val === "string" && val.trim()) {
      return val.split(/[\n|]/).map((r) => r.trim()).filter(Boolean);
    }
    return null;
  }, [themeData]);

  if (loadingQuestions) {
    return (
      <div className="game-loading" style={themeVars}>
        <p>Loading questions...</p>
      </div>
    );
  }

  const radioOptions = currentQuestion
    ? [
        currentQuestion.option_one,
        currentQuestion.option_two,
        currentQuestion.type_question === "radioselect" ? currentQuestion.option_three : "",
      ].filter(Boolean)
    : [];

  const defaultRules = [
    "Click on any state on the map to start answering",
    `Earn ${rightPoints} points for each correct answer`,
    "Complete all 28 states to finish the activity",
  ];
  const rulesList = adminRules || defaultRules;

  const progressPct = (completedStates.length / PLAYABLE_STATES.length) * 100;

  return (
    <div
      className="game-split-container"
      style={{
        ...themeVars,
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none",
      }}
    >
      {confettiActive && <ConfettiBurst onDone={() => setConfettiActive(false)} />}

      {/* ── Timer badge – top left ── */}
      <div className="game-timer-badge">
        <div className="gbadge-icon timer-icon">
          <ClockIcon />
        </div>
        <div className="gbadge-text">
          <span className="gbadge-value">{formatTime(elapsed)}</span>
          <span className="gbadge-label"></span>
        </div>
      </div>

      {/* ── Score badge – top right ── */}
      <div className="game-score-badge">
        <div className="gbadge-icon score-icon">
          <StarIcon />
        </div>
        <div className="gbadge-text">
          <span className="gbadge-value">{score}</span>
          <span className="gbadge-label"></span>
        </div>
      </div>

      {/* ── Background sound – speaker toggle ── */}
      {soundEnabled && soundUrl && (
        <>
          <audio ref={soundRef} src={soundUrl} loop />
          <button
            type="button"
            className="game-sound-toggle"
            onClick={toggleSound}
            aria-label={soundMuted ? "Unmute background sound" : "Mute background sound"}
            title={soundMuted ? "Unmute background sound" : "Mute background sound"}
          >
            {soundMuted ? <SpeakerMutedIcon /> : <SpeakerOnIcon />}
          </button>
        </>
      )}

      {/* ── Main content ── */}
      <div className="game-content-wrapper">

        {/* ── Left – Map inside white card ── */}
        <div className="game-left-side">
          <div
            className="map-card"
            style={{
              ...(mapBgImageUrl ? {
                background: `url("${mapBgImageUrl}") center/cover no-repeat`,
                backdropFilter: "none",
              } : {}),
              ...(mapCardHeight ? { minHeight: mapCardHeight } : {}),
            }}
          >
            <div className="map-container">
              <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 570 600" className="india-map-svg">
                <g id="regions">
                  {ALL_MAP_IDS.map((mapId) => {
                    const isDecorative = Boolean(DECORATIVE_MAP_REGIONS[mapId]);
                    return (
                      <path
                        key={mapId}
                        id={mapId}
                        className={isDecorative ? "state-path state-path--decorative" : "state-path"}
                        fill={activeGif?.mapId === mapId ? "transparent" : getStateFill(mapId)}
                        stroke="#000000"
                        strokeWidth="1"
                        vectorEffect="non-scaling-stroke"
                        onClick={isDecorative ? undefined : () => handleStateClick(mapId)}
                        onMouseEnter={isDecorative ? undefined : () => setHoveredState(mapId)}
                        onMouseLeave={isDecorative ? undefined : () => setHoveredState(null)}
                        d={getPathData(mapId)}
                      />
                    );
                  })}
                </g>
                <g className="island-labels-group">
                  {ISLAND_LABELS.map((island) => (
                    <MapLabel key={island.id} x={island.x} y={island.y} lines={island.lines} className="island-label" />
                  ))}
                  {SEA_LABELS.map((sea) => (
                    <MapLabel key={sea.id} x={sea.x} y={sea.y} lines={sea.lines} className="sea-label" />
                  ))}
                </g>
                {hoveredState && STATE_MAP[hoveredState] && hoveredState !== activeGif?.mapId && (
                  <StateHoverLabel mapId={hoveredState} label={STATE_MAP[hoveredState]} />
                )}
              </svg>

              {activeGif && STATE_GIF_BOUNDS[activeGif.mapId] && (
                <StateGifPopout
                  key={gifKey}
                  mapId={activeGif.mapId}
                  gifUrl={`${process.env.REACT_APP_S3_PATH}${activeGif.gifUrl}`}
                />
              )}
            </div>

            {/* ── Secret Message – fits inside the map card ── */}
            {secretEnabled && (
              <div className="secret-message-section">
                <div className="secret-message-boxes">
                  {secretRevealed.map((letter, i) =>
                    letter === " " ? (
                      <span key={i} className="secret-letter-space" />
                    ) : (
                      <div key={i} className={`secret-letter-box${letter ? " is-revealed" : ""}`}>
                        {letter || ""}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Right – Question panel ── */}
        <div
          className="game-right-side"
          ref={questionSideRef}
          style={
            mapBgImageUrl && mapBgApplyQuestion ? {
              background: `url("${mapBgImageUrl}") center/cover no-repeat`,
            } : {}
          }
        >
          <div className="question-content-area">
            {inlineNotice && (
              <div className="inline-notice" role="status">{inlineNotice}</div>
            )}

            {showInstructions || !currentQuestion ? (
              /* Instructions with dynamic rules */
              <div className="instructions-panel">
                <div className="instructions-header">
                  <div className="state-pin-circle">
                    <PinIcon />
                  </div>
                  <div>
                    <h2 className="instructions-title">Welcome to 28 States!</h2>
                    <p className="instructions-sub">Explore India, one state at a time</p>
                  </div>
                </div>
                <hr className="q-divider" />
                <ul className="rules-list">
                  {rulesList.map((rule, i) => (
                    <li key={i}>{rule}</li>
                  ))}
                </ul>
              </div>

            ) : currentQuestion ? (
              /* Question display */
              <div className="question-display">
                {/* State header */}
                <div className="q-state-header">
                  <div className="state-pin-circle">
                    <PinIcon />
                  </div>
                  <div className="q-state-info">
                    <h1 className="state-name-display">{toTitleCase(selectedState)}</h1>
                    <p className="state-tagline">
                      <span className="sparkle-gold">✦</span>
                      {currentQuestion.question_title || "Land of India"}
                      <span className="sparkle-gold">✦</span>
                    </p>
                  </div>
                </div>
                <hr className="q-divider" />

                {currentQuestion.image && (
                  <div className="question-image-display">
                    <img
                      src={
                        currentQuestion.image.startsWith("http")
                          ? currentQuestion.image
                          : `${process.env.REACT_APP_S3_PATH}${currentQuestion.image}`
                      }
                      alt="Question"
                    />
                  </div>
                )}

                <div className="question-text-display">
                  <p>{currentQuestion.question}</p>
                </div>

                {/* Radio options – radio on RIGHT */}
                {(currentQuestion.type_question === "radioselect" ||
                  currentQuestion.type_question === "truefalse") && (
                  <div className="options-display">
                    {radioOptions.map((opt) => {
                      const isCorrectOpt = answerSubmitted && opt.toLowerCase() === currentQuestion.correct_answer.toLowerCase();
                      const isWrongOpt = answerSubmitted && selectedAnswer === opt && !feedback?.isCorrect;
                      return (
                        <label
                          key={opt}
                          className={[
                            "option-label",
                            selectedAnswer === opt && !answerSubmitted ? "is-selected" : "",
                            answerSubmitted ? "is-locked" : "",
                            isCorrectOpt ? "is-correct" : "",
                            isWrongOpt ? "is-wrong" : "",
                          ].filter(Boolean).join(" ")}
                        >
                          <span className="opt-text">{opt}</span>
                          <input
                            type="radio"
                            name="answer"
                            value={opt}
                            checked={selectedAnswer === opt}
                            disabled={answerSubmitted}
                            onChange={(e) => setSelectedAnswer(e.target.value)}
                          />
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* Type text input */}
                {currentQuestion.type_question === "typetext" && (
                  <div className="text-input-display">
                    <input
                      type="text"
                      placeholder="Type your answer here"
                      value={textAnswer}
                      disabled={answerSubmitted}
                      className={
                        answerSubmitted ? (feedback?.isCorrect ? "is-correct" : "is-wrong") : ""
                      }
                      onChange={(e) => {
                        setTextAnswer(e.target.value);
                        if (validationMsg) setValidationMsg("");
                      }}
                    />
                  </div>
                )}

                {validationMsg && (
                  <p className="validation-msg" role="alert">{validationMsg}</p>
                )}

                {feedback && (feedback.didYouKnow || !feedback.isCorrect) && (
                  <div className="answer-feedback">
                    {!feedback.isCorrect && (
                      <p className="feedback-correct-answer">
                        Correct answer is: <strong>{feedback.correctAnswer}</strong>
                      </p>
                    )}
                    {feedback.didYouKnow && (
                      <div className="did-you-know-block">
                        <p className="dyk-heading">Did you know?</p>
                        <p className="dyk-message">{feedback.didYouKnow}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Submit / Next button with sparkles */}
                <div className="button-area">
                  <span className="btn-sparkle">✦</span>
                  <button
                    className="next-button"
                    onClick={handleAction}
                    disabled={answerSubmitted && !canProceed}
                  >
                    {answerSubmitted
                      ? currentQuestionIndex >= questionsByState[selectedState].length - 1
                        ? "Next State"
                        : "Next Question"
                      : "Submit Answer"}
                  </button>
                  <span className="btn-sparkle">✦</span>
                </div>

                <div className="question-progress">
                  Question {currentQuestionIndex + 1} of {questionsByState[selectedState].length}
                </div>
              </div>
            ) : null}
          </div>

          {/* ── Journey Progress – below the question panel ── */}
          <div className="journey-progress-section">
            <div className="journey-progress-card">
              <img src="/img/bag-1 copy.png" alt="bag" className="journey-bag-img" />
              <div className="journey-content">
                <div className="journey-title">
                  Journey Progress
                  <span className="journey-plane"> ✈</span>
                </div>
                <div className="journey-bar-outer">
                  <div
                    className="journey-bar-fill"
                    style={{ width: `${progressPct}%` }}
                  >
                    {completedStates.length > 0 && <span className="journey-arrow">→</span>}
                  </div>
                </div>
                <div className="journey-count">{completedStates.length}/{PLAYABLE_STATES.length} States</div>
              </div>
              <img src="/img/stone copy.png" alt="" className="journey-stone-img" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function stateCenterToSvg(mapId) {
  const center = STATE_CENTERS[mapId];
  if (!center) return { x: 0, y: 0 };
  return { x: (center.x / 100) * 570, y: (center.y / 100) * 600 };
}

const SVG_VIEWBOX = { width: 570, height: 600 };
const GIF_POPOUT_EXPAND = 200;

function gifBoundsToStyle(bounds, expanded) {
  const x = expanded ? bounds.x / 2 : bounds.x;
  const y = expanded ? bounds.y / 2 : bounds.y;
  const width = expanded ? bounds.width + GIF_POPOUT_EXPAND : bounds.width;
  const height = expanded ? bounds.height + GIF_POPOUT_EXPAND : bounds.height;

  return {
    left: `${(x / SVG_VIEWBOX.width) * 100}%`,
    top: `${(y / SVG_VIEWBOX.height) * 100}%`,
    width: `${(width / SVG_VIEWBOX.width) * 100}%`,
    height: `${(height / SVG_VIEWBOX.height) * 100}%`,
  };
}

function ConfettiBurst({ onDone }) {
  const canvasRef = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const colors = ["#E25569", "#FB9946", "#FFD93D", "#6BCB77", "#4D96FF", "#FF6B9D", "#C77DFF"];
    const particles = [];

    [{ x: 0, y: canvas.height, dir: 1 }, { x: canvas.width, y: canvas.height, dir: -1 }].forEach((origin) => {
      for (let i = 0; i < 90; i += 1) {
        particles.push({
          x: origin.x,
          y: origin.y,
          vx: origin.dir * (Math.random() * 8 + 3) + (Math.random() - 0.5) * 6,
          vy: -(Math.random() * 14 + 6),
          w: Math.random() * 9 + 4,
          h: Math.random() * 6 + 3,
          color: colors[Math.floor(Math.random() * colors.length)],
          rotation: Math.random() * 360,
          spin: (Math.random() - 0.5) * 12,
          life: 1,
          gravity: 0.22,
        });
      }
    });

    let frameId;
    let ticks = 0;
    const maxTicks = 180;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        if (p.life <= 0) return;
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.spin;
        p.life -= 0.007;
        ctx.save();
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      ticks += 1;
      if (ticks < maxTicks) {
        frameId = requestAnimationFrame(draw);
      } else {
        onDoneRef.current?.();
      }
    };

    frameId = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(frameId);
    };
  }, []);

  return <canvas ref={canvasRef} className="confetti-canvas" aria-hidden="true" />;
}

function StateGifPopout({ mapId, gifUrl }) {
  const bounds = STATE_GIF_BOUNDS[mapId];
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    setExpanded(false);
    const frame = requestAnimationFrame(() => setExpanded(true));
    return () => cancelAnimationFrame(frame);
  }, [mapId, gifUrl]);

  return (
    <img
      src={gifUrl}
      alt=""
      className={`state-gif-popout${expanded ? " is-expanded" : ""}`}
      style={gifBoundsToStyle(bounds, expanded)}
    />
  );
}

function StateHoverLabel({ mapId, label }) {
  const { x, y } = stateCenterToSvg(mapId);
  const textWidth = Math.max(label.length * 7.4, 46);
  const padX = 8;
  const padY = 5;
  const boxW = textWidth + padX * 2;
  const boxH = 19 + padY * 2;

  return (
    <g className="state-hover-tooltip" pointerEvents="none">
      <rect
        x={x - boxW / 2}
        y={y - boxH / 2}
        width={boxW}
        height={boxH}
        rx={5}
        ry={5}
      />
      <text x={x} y={y + 5} textAnchor="middle">
        {label}
      </text>
    </g>
  );
}

/** Decorative, non-clickable map label (island/sea name) — text only, no shape. */
function MapLabel({ x, y, lines, className }) {
  return (
    <text x={x} y={y} textAnchor="middle" className={className} pointerEvents="none">
      {lines.map((line, i) => (
        <tspan key={line} x={x} dy={i === 0 ? 0 : 11}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

// SVG Path data for each state
function getPathData(mapId) {
  const paths = {
    map_1: "M318.82,370.45L312.65,379.11L309.63,381.75L311.02,381.16L307.04,385.15L304.46,384.96L306.61,385.34L306.73,386.14L296.3,391.95L296.24,392.95L294.22,393.82L292.38,397.81L290.42,399.75L289.62,399.49L289.98,400.46L277.76,407.18L272.94,411.85L272.01,414.61L274.13,415.84L273.87,412.89L274.32,414.77L273.3,417.88L274.18,418.34L272.36,421.12L273.17,421.3L270.3,422.53L270.69,421.18L269.91,422.72L262.24,426.29L262.22,425.31L261.93,426.38L259.1,424.73L256.27,425.7L256.34,424.8L254.55,425.24L253.31,426.41L251.59,432.99L250.67,432.5L251.34,433.26L248.67,436.02L248.88,437.26L247.63,436.36L248.54,437.2L247.59,437.93L246.36,436.09L247.17,437.97L245.75,438.39L244.21,434.89L239.68,435.34L235.31,438.88L234.31,441.6L232.67,441.37L233.6,441.34L234.06,442.65L232.01,446.05L231,451.62L231.58,456.46L233.53,460.76L231.89,461.99L233.77,461.4L232.55,469.75L234.79,477.02L234.65,483.47L231.65,483L231.74,481.88L230.19,482.03L231.05,482.87L229.89,483.22L229,485.87L225.2,487.16L226.36,488.04L225.63,488.71L224.22,486.96L222.3,487.62L221.75,486.38L219.54,486.17L218.75,486.55L219.62,486.88L219.61,488.93L217.51,490.7L215.77,490.09L214.62,492.51L211.44,491.13L209.58,492.27L209.61,491.02L206.23,491.55L206.07,492.59L204.71,493.03L205.2,494.31L203.45,498.78L202.09,498.12L202.64,498.62L201.72,500.38L200.02,500.5L198.09,498.89L196.69,499.06L197.65,495.77L198.8,495.77L199.59,494.14L201.67,495.38L200.62,494.04L203.75,489.53L203.9,487.41L199.98,486.25L200.41,481.06L196.65,481.51L196.09,479.72L194.63,479.92L195.1,475.53L193.89,474.87L192.16,476.12L192.72,473.64L191.47,474.66L189.66,473.98L189.91,475.4L187.77,476.81L187.57,478.01L186.58,477.11L186.35,477.93L184.17,477.76L184.21,478.89L182.87,479.13L182.39,475.93L179.15,475.85L178.95,474.75L177.74,475.41L177.78,474.28L176.81,475.78L177.59,477.56L174.34,477.81L173.9,476.44L175.14,474.12L173.19,472.17L174,471.31L172.44,469.43L174.84,469.15L174.91,471.55L176.73,471.77L177.35,472.81L180.32,472.12L180.97,474.69L181.88,475.01L182.5,473.24L180.56,472.15L181.84,470.53L180.95,470.11L183.98,469.15L183.72,467.14L181.83,465.99L181.76,468.77L181.16,467.19L179.68,467.06L179.71,465.95L176.92,465.97L176.24,468.41L173.31,467.8L172.28,464.78L173.37,464.59L173.96,463L172.04,463.21L170,460.64L170.36,456.96L171.6,456.49L171.94,453.77L170.04,453.12L170.21,451.2L170.66,450.69L172.15,451.93L175.92,452.51L177.32,450.08L176.7,448.05L177.53,447.46L176.49,445.92L175.59,446.03L173.89,442.63L174.94,442.45L174.94,439.85L176.77,439.25L175.31,437.92L175.62,435.95L174.85,435.78L175.86,434.21L179.07,433.24L189.33,435.08L190.98,434.46L193.11,435.2L193.61,434.32L195.12,435.84L197.6,433.62L197.76,431.84L200.63,430.72L203.49,431.21L204.57,430.36L207.51,431.87L208.57,429.29L210.03,430.22L210.31,428.27L212.07,427.42L215.56,427.69L216.65,421.04L219.64,420.61L226.12,417.64L229.53,419.39L231.6,415.94L230.15,414.95L230.48,414.06L233.69,411.29L235.18,411.97L237.25,415.98L237.75,415.2L239.42,416.69L241.43,416.5L240.63,415.7L241.18,413.65L237.01,412.78L237.13,411.05L237.66,410.62L238.61,411.84L240.73,409.4L242.33,410.28L242.1,410.99L245.8,411.65L247.15,408.12L248.63,408.56L251.94,407.48L252.29,405.68L253.88,405.72L255.05,404.27L256.64,404.88L258.09,403.13L259.56,397.7L263.45,395.42L263.01,394.19L263.8,393.33L267.96,390.87L270.54,391.19L271.99,392.44L272.46,391.06L273.75,391.08L274.29,389.22L273.19,388.09L274.8,385.87L273.72,385.65L276.31,381.23L277.79,383.22L277.45,384.19L278.81,384.96L278.22,385.85L279.24,387.43L281.76,385.36L282.36,383.18L283.94,383.72L284.28,384.86L287.13,384.43L286.39,383.01L287.73,381.3L286.69,381L287.01,379.67L293.73,374.73L292.8,372.84L292.01,373.34L292.31,371.77L294.55,372.99L294.62,370.5L295.96,371.79L297.8,368.99L299.95,373.54L300.58,373.75L299.86,372.22L300.74,371.74L302.26,375.54L305.18,375.86L306.38,377.01L310.85,376.06L310.89,374.62L312.65,373.62L312.66,371.86L316.84,370.83L315.72,369.4L317.72,368.84L318.82,370.45Z",
    map_2: "M513.49,217.4L512.72,210.2L517.6,208.34L518.59,205.71L520.06,206.46L525.08,205.16L525.78,205.77L527.92,203.76L527.18,202.29L524.88,202.42L525.48,199.94L523.73,198.52L523.2,196.34L527.28,191.56L520.39,191.76L516.26,194.09L515,193.5L506.62,196.02L499.07,199.79L494.9,198.28L495.36,200.47L489.72,205.08L486.96,207.96L487.48,209.44L484.49,211.6L472.29,212.67L469.57,210.83L465.6,210.21L464.23,211.75L456.98,213.17L455.36,213.11L455.28,210.82L453.88,209.39L454.11,205.7L455.58,205.42L453.73,201.31L446.94,201.24L445.17,198.2L446.39,196.74L446.48,194.83L444.95,193.9L450.19,194.7L451.88,196.56L457.11,193.7L458.36,193.77L459.26,195.29L462.7,194.02L463.8,194.44L467.13,191.04L465.71,189.56L466.03,188.45L471.32,185.56L471.99,186.18L472.39,184.79L475.83,184.32L476.29,182.76L475.38,181.97L477.12,180.66L478.26,177.93L487.68,176.58L489.36,177.2L490.49,173.88L493.47,171.6L494.58,168.99L498.61,166.12L500.97,166.08L502.28,163.66L504.84,165.6L505.42,167.38L506.8,166.98L509.6,168.07L509.27,167.27L513.83,168.5L514.12,169.55L517.61,169.97L520.31,165.44L529.27,161.38L532.11,165.83L534.51,164.98L535.06,165.57L534.19,167.29L529.88,169.17L531.18,170.98L530.84,172.64L534.19,170.1L537.51,169.15L536.62,170.87L539.18,175.23L533.75,180.23L534.54,181.26L532.69,182.66L534.61,182.98L535.14,183.99L536.23,182.48L540.07,181.55L542.1,183.41L544.41,183.18L546.23,184.59L548.01,183.51L553.73,186.95L552.43,187.7L552,189.43L553.99,190.51L553.5,193.09L551.04,192.82L544.36,198.63L544.62,201.82L549.44,208.42L547.41,208.93L543.76,207.13L544.12,205.75L540.91,203.46L539.2,203.62L537.53,205.12L532.03,205.37L528.35,207.15L526.48,210.04L524.23,210.65L522.66,213.36L521.59,212.9L520.44,214.7L518.98,214.49L516.94,217.2L514.7,218.01L513.49,217.4Z",
    map_3: "M458.81,266.33L457.25,266.38L458.37,263.58L457.43,259.11L458.56,255.51L457.82,253.24L462.55,253.89L462.38,252.39L461.11,251.39L462.31,249.1L464.86,248.91L468.23,246.82L467.52,246.26L467.89,244.58L463.94,241.93L465.46,239.35L463.96,239.99L460.45,236.22L456.48,237.84L456.17,234.89L457.51,233.11L456.24,232.23L458.94,229.58L451.96,231.12L449.98,228.72L448.34,229.92L447.19,233.02L446.11,232.32L446.5,230.66L445.46,230.44L443.73,233.87L440.93,234.49L438.91,236.67L438.34,234.02L434.87,234.71L435.3,233.36L434.15,233.37L433.75,232.33L431.42,232.14L430.63,232.94L430.07,231.96L428.93,232.09L427.79,233.13L426.93,231.94L425.94,233.09L425.05,230.79L418.3,231.95L414.24,236.24L416.52,238.97L414.06,239.99L413.43,241.63L413.93,238.84L412.73,234.77L414.01,232.34L412.96,232.16L413.71,231.57L412.73,231.31L412.15,228.95L410.62,227.37L411.01,225.29L413.54,223.35L413.65,216.25L418.58,216.01L419.71,215.57L420.2,213.9L423.82,212.93L429.2,215.63L435.78,215.4L436.44,214.62L441.03,215.4L442.37,214.28L443.93,215.2L447.4,214.9L450.64,212.74L454.38,214.04L455.36,213.11L464.23,211.75L465.6,210.21L469.57,210.83L472.29,212.67L484.11,211.69L487.33,209.63L486.96,207.96L495.36,200.47L494.9,198.28L499.07,199.79L506.62,196.02L515,193.5L516.26,194.09L520.39,191.76L527.28,191.56L523.2,196.34L523.73,198.52L525.48,199.94L524.88,202.42L527.18,202.29L527.92,203.76L525.78,205.77L525.08,205.16L520.06,206.46L518.59,205.71L517.6,208.34L512.72,210.2L509.48,212.57L507.25,212.26L504.56,215.64L499.19,217.7L496.38,221.86L495.74,219.83L493.96,221.91L493.75,224.25L492.5,224.61L490.48,227.99L490.1,232.8L486.68,234.91L486.27,231.86L484.68,232.99L485.03,234.13L478.09,240.24L480.38,242.4L480.7,245.08L476.51,250.92L475.47,255.2L473.97,255.28L472.6,263.15L468.77,263.84L467.51,260.93L466.15,264.24L464.93,264.68L464.66,266.31L463.52,266.46L462.24,268.53L461.4,268.34L461.14,266.31L458.81,266.33Z",
    map_4: "M302.05,204L301.65,202.05L305.01,202.18L306.71,200.46L310,203.21L316.18,204.17L317.39,206.45L316.55,210.01L322.41,211.7L324.13,213.19L323.63,213.85L326.76,213.59L327.17,215.82L329.39,216.16L334.97,213.52L336.63,214.64L336.93,218.1L339.09,219.62L342.28,217.67L345.84,219.21L348.15,218.67L355.39,222.54L358.4,221.79L361.66,219.28L362.04,221.98L366.66,224.08L368.92,222.24L371.65,223.42L377.09,221.48L379.49,223.93L381,220.24L381.74,220.8L383.34,219.98L382.37,221.14L384.41,224.02L380.42,227.44L378.47,227.98L378.28,229.4L376.1,230.19L375.38,232.68L375.73,233.68L377.37,234.07L377.12,235.68L379.97,237.41L379.43,239.48L380.28,241.22L377.61,240.43L374.67,242.68L375.14,244.73L376.35,245.62L375.85,247.11L371.2,244.29L370.46,245.55L369.38,245.28L369.21,247.3L366.45,246.73L365.84,249.42L364.36,249.41L363.16,250.73L363.37,253.52L361.92,254.75L361.36,259.13L359.13,258.4L358.7,260.4L356.87,259.99L356.56,258.96L354.27,260.06L352.99,259.28L351.24,260.92L350.14,263.93L347.03,262.05L347.61,259.64L344.32,259.2L344.16,257.16L342.74,255.69L340.92,256.62L339.58,255.25L336.96,254.8L335.33,257.53L335.62,259.64L333.98,259.22L332.89,260.8L328.51,260.76L324.98,263.68L324.68,262.51L322.29,263.88L321.46,263.87L321.03,262.05L320.18,262.25L319.46,260.71L316.91,263.44L314.37,263.79L313.85,265.59L310.12,262.3L310.74,261.25L310.12,259.98L308.15,260.32L308.01,261.1L307.39,260.5L306.72,261.68L304.53,258.54L302.22,260.65L295.35,260.78L296.1,258.75L295.01,256.54L293.28,255.61L292.03,250.95L292.59,247.26L301.68,242.47L301.16,241.88L303.22,239.95L306.11,238.43L306.44,236.76L310.08,237.91L310.85,236.27L313.77,237.61L314.96,237.48L315.02,236.43L316.41,236.59L314.52,233.59L307.9,231.16L305.03,228.21L304.8,226.43L307.55,226.2L307.89,223.61L302.89,222.08L302.89,220.71L305.59,220.2L306.2,218.24L309.76,219.07L312.41,218.51L310.25,215.97L309.21,216.43L309.28,213.85L307.85,214.34L306.98,213.17L305.68,213.19L305.47,210.11L304.55,209.58L305.1,209.09L303.61,209.21L304.32,206.98L302.89,205.94L303.01,204.36L301.55,204.42L302.05,204Z",
    map_5: "M235.26,377.65L234.79,376.81L236.72,375.78L235.26,372.24L237.33,367.3L240.69,363.98L241.52,365.7L244.22,366.2L245.96,364.61L244.94,363.39L246.88,362.6L245.53,360.6L242.6,359.73L242.67,358.05L239.91,355.31L238.83,355.66L239.17,356.45L237.72,356.02L239.53,354.51L237.83,353.23L239.96,353.26L240.44,350.55L239.54,349.08L237.6,348.98L237.4,347.01L241.75,345.32L241.85,339.73L239.23,339.46L241.03,338.22L240.23,333.14L238.81,333.21L238.16,331.57L238.78,328.29L242.79,325.76L243.86,322.27L243.79,317.06L245.36,316.73L245.5,312.52L247.13,309.35L248.48,310.67L248.96,310.24L248.33,309.34L249.17,308.98L249.24,307.04L251,305.72L250.92,302.77L252.05,301.83L253.04,302.56L254.53,301.95L254.89,301.1L256.25,302.76L258.47,300.84L260.45,300.7L261.5,298.58L263.07,298.29L263.19,294.06L266.37,292.38L266.33,289.95L269.87,289.26L271,285.79L269.31,283.52L267.09,283.24L265.93,280.82L264.27,281.24L262.45,280.16L260.36,281.57L259.48,279.69L261.67,277.01L260.19,273.36L261.19,272.91L263.26,275.15L265.93,273.96L268.33,275.01L271.28,274.36L271.6,275.22L277.08,275.73L282.3,272.17L284.94,273.88L288.45,273.6L292.1,269.34L293.99,269.69L295.55,270.87L296.51,274.13L299.01,274.98L300.48,279.43L303.46,280.16L303.54,278.96L304.8,278.76L304.05,283.96L305.97,284.84L305.29,288.7L307.02,289.77L307.41,292.2L308.19,291L308.7,291.95L311.55,291.98L311.84,293.15L308.65,298.22L304.76,300.14L304.22,301.49L305.43,302.26L304.68,304.16L298.08,307.08L296.43,309.59L297.23,310.28L295.97,310.91L296.02,312.33L297.23,313.39L296.94,314.74L294.76,315.97L295.07,316.79L293.87,318.13L295.08,319.12L293.15,319.38L292.29,321.74L293.56,324.7L291.09,324.17L291.05,326.31L288.3,329.68L285.26,328.18L279.3,328.71L279.05,330.98L276.08,335.19L273.92,334.06L274.31,339.17L273.5,340.69L275.46,343.27L274.75,350.63L280.51,351.96L280.47,355.3L278.36,356.42L278.59,354.63L275.71,353.77L273.8,355.24L271.77,351.87L268.56,350.83L267.94,351.63L266.44,349.79L264.88,351.37L264.85,353.86L268.6,356.19L267.9,361.85L269.2,361.69L269.7,363.41L270.9,363.64L270.11,364.58L270.89,365.51L270.46,369.39L271.43,370.11L271.94,373.73L270.67,374.11L270.49,376.13L268.95,377.72L267.05,378.06L266.52,379.4L265.51,378.9L266.51,380.8L263.07,383.77L262.75,385.1L258.72,386.84L257.77,392.57L256.06,395.94L251.87,395.02L249.5,396.29L247.86,390.44L248.41,388.68L246.33,389.4L245.62,387.37L245.14,388.73L243.96,388.62L244.95,386.96L242.06,381.64L239.37,379.48L236.65,380.29L235.26,377.65Z",
    map_6: "M120.22,454.71L116.98,450.81L117.8,449.71L116.33,445.3L114.69,444.59L117.4,443.94L114.94,443.44L115.49,442.44L114.21,442.55L113.92,439.86L112.55,437.96L115.31,437.72L115.92,436.51L117.41,437.64L118.48,440.31L121.98,438.86L123.43,440.14L123.22,442.71L124.82,446.67L123.25,447.38L124.45,448.86L124.09,451.76L122.4,454.15L121.66,453.55L121.61,454.39L120.22,454.71Z",
    map_7: "M93.62,322.31L94.55,321.12L92.78,320.56L93.07,319.36L96.88,318.23L91.13,318.09L93.08,313.48L92.65,312.91L92.02,314.03L90.83,313.7L91.57,308.63L92.79,307.27L95.3,308.16L96.26,306.84L97.56,307.14L98.49,306.22L95.45,306.99L91.38,306.13L90.46,307.67L90.57,306.76L88.72,305.45L88.72,303.94L89.13,307.07L88.16,306.45L88.59,308.43L87.75,308.92L87.2,308.01L86.5,313.05L85.88,310.62L87,309.49L85.12,311.56L87.08,319.13L82.91,326.84L83.53,327.71L72.27,332.91L70.93,334.43L59.74,338L54.27,335.78L48.87,332.21L37.22,319.38L30,312.88L24.41,305.36L27.09,302L26.89,303.68L29.16,303.14L28.54,305.34L29.76,306.45L31.93,304.99L34.69,304.83L35.07,303.07L37.09,304.59L39.14,302.1L40.61,303.49L43.74,300.72L47.23,300.54L53.37,289.99L51.58,290.98L50.96,292.81L48.58,291.92L46.25,292.27L46.17,293.34L40.51,294.51L38.83,296.78L29.27,294.76L19.14,288.38L17.88,286.82L19.29,285.55L16.32,281.93L16.11,278.71L20.67,274.63L19.5,274.16L16.95,276.27L16.61,275.73L18.53,273.05L17.26,272.02L21.02,271.98L21.2,265.44L22.4,264.99L22.98,267.08L24.55,265.28L25.7,266.87L27.31,265.88L29.19,266.58L36.53,265.5L39.18,267.91L44.5,267.95L46.21,265.45L54.86,262.87L55.02,266.36L59.7,266.76L61.85,264.27L65.06,263.16L62.79,262.39L62.57,260.34L62.99,258.54L64.61,257.54L68.17,259.03L71.72,257.71L74.95,258.54L77.52,257.79L78.62,259.13L78.86,257.76L80.05,258.68L82.25,257.1L84.28,258.92L87.87,258.88L85.95,259.64L89.52,261.18L89.93,263.14L90.73,263.09L91.37,261.18L94.19,262.08L94.92,264.08L98.49,264.79L99.75,264.04L100.15,261.65L101.76,261.47L101.49,263.43L104.22,264.03L101.44,267.65L104.07,269.4L103.64,270.47L104.61,271.15L106.79,269.12L107.73,272.93L106.62,275.58L109.25,277.3L109.46,279.11L110.65,278.33L112.21,278.99L111.68,282.42L113.01,282.36L113.42,283.22L114.14,282.3L115.3,282.54L116.55,284.75L117.82,283.86L120.51,285.62L120.89,287.94L123.4,288.09L125.26,292.9L127.14,293.3L125.61,298.83L123.45,298.7L121.36,301.26L119.34,300.76L119.19,301.78L120.09,301.44L120.79,303.21L122.06,302.06L123.61,303.77L122.06,305.2L119.84,304.42L119.91,307.21L120.79,307.39L120.97,309.74L121.86,309.94L120.38,311.31L121.21,312.6L114.95,315.23L116.58,318.37L114.52,319.22L115.88,321.82L119.75,320.55L122.34,321.15L124.29,320.42L124.7,321.35L122.63,322.57L119.79,322.15L119.35,323.38L117.77,323.89L117.51,325.81L115.34,326.42L115.16,328.34L111.69,329.13L110.79,328.42L114.19,330.43L115.07,330.1L114.79,330.88L116.72,332.14L117.52,336.99L115.15,337.97L115.6,339.38L113.76,340.51L112.23,340.6L108.36,337.54L107.32,338.99L109.14,341.05L107.1,344.07L107.76,347.87L105.6,347.75L103.82,349.46L103.71,347.95L101.14,348.62L101.1,347.46L103.13,346.15L101.68,344.75L100.65,346.03L98.49,346.19L99.23,347.64L97.39,347.43L96.08,349.39L94.95,349.17L95.5,345.26L98,344.43L98.17,343.56L97.09,342.37L97.86,340.41L95.95,333.8L96.72,332.79L95.45,333.18L94.83,331.1L95.43,330.74L94.29,330.46L94.7,328.95L93.76,328.69L93.03,329.97L92.34,325.78L94.33,322.43L93.4,323.03L93.62,322.31Z",
    map_8: "M171.26,136.8L171.76,134.65L170.2,132.62L171.09,131.87L174.58,134.22L174.86,135.67L177.47,136.75L177.54,138.55L176.68,139.2L178.31,140.87L181.96,142.15L182.9,141.25L182.7,142.5L184.09,141.59L185.62,143.16L182.2,148.57L179.62,149.66L176.78,155.15L177.23,156.64L176.15,160.03L177.44,164.39L176.88,168.73L178.54,170.66L178.68,172.63L177.14,173.97L175.97,173.1L173.48,174.39L173.32,178.07L171.44,179.18L172.3,180.66L175.89,180.44L177.63,182.7L180.83,180.67L183.37,182.6L183.23,185.49L184.56,185.8L183.64,186.66L184.63,187.27L183.26,189.19L184.51,191.95L178.89,195.24L175.24,194.55L175.97,196.21L174.42,196.12L173.97,197.83L172.84,197.83L172.36,196.34L173.82,188.02L172,186.39L168.04,188.97L168.2,190.53L166.09,191.49L165.94,190.12L164.4,189.97L165.18,189.61L164.27,189.03L165.18,188.76L164.71,187.9L161.12,187.31L162.54,188.32L161.45,188.87L162.44,189.38L162.19,190.31L159.22,189.71L159.7,190.29L158.78,191.05L160.07,194.11L159.13,194.78L157.55,193.55L155.45,193.63L154.49,192.25L156.5,189.21L155.63,189.88L154.73,189.01L156.44,187.42L157.65,187.78L154.48,183.47L151.65,182.68L151.8,181.67L147.66,178.5L146.02,172.22L146.85,170.52L145.42,170.4L144.06,167.86L144.71,165.45L143.2,165.98L143.28,164.82L141.5,165.99L140.69,165.31L138.79,166.04L138.08,164.93L136.47,165.03L135.99,163.37L134.54,162.59L129.82,164.08L128.58,161.64L130.12,160.22L129.35,158.89L130.01,155.53L127.53,155.7L127.4,154.82L129.05,153.25L128.41,151.68L130.65,152.48L131.7,151.12L133.66,150.7L135.8,151.58L137.17,153.46L138.94,152.28L139.04,154.46L140,155.06L141.1,154L141.66,155.57L140.29,157.37L141.78,159.61L142.76,159.41L143.27,157.21L145.63,154.45L147.94,155.77L150.34,155.52L150.5,154.45L151.72,154.11L153.72,155.69L156.74,155.66L160.48,153.25L159.07,152.04L160.71,148.47L159.75,147.53L162.09,148.4L162.05,147.6L163.24,148.04L163.62,146.54L164.4,148.58L165.91,148.95L167.88,146.89L166.15,145.4L169.46,143.68L169.49,142.15L171.49,141.88L172.5,143.4L173.25,142.79L172.55,141.82L173,138.54L171.26,136.8Z",
    map_9: "M153.7,98.66L154.65,97.3L154.45,95.01L152.51,93.06L152.51,91.52L154.85,92.44L157.84,90.05L160.5,89.47L163.25,86.44L169.67,86.52L170.55,85.02L171.52,85.25L171.04,87L173.16,89.47L177.23,90.55L177.62,91.9L180.67,93.74L182.05,92.43L183.16,92.95L187.07,90.45L187.95,90.74L189.39,92.08L188.86,92.89L191.71,94.89L191.49,96.36L193.04,98.48L200.23,95.02L200.52,97.78L198.8,98.7L198.46,100.1L199.06,100.75L200.4,99.66L201.31,100.06L201.43,101.14L202.93,101.91L201.44,105.37L204.3,105.94L204.36,107.86L207.61,110.35L206.12,114.51L208.71,118.1L206.4,120L208.04,121.38L207.68,122.39L211.78,128.13L209.61,128.41L208.03,126.37L204.41,125.75L201.98,126.39L199.87,124.64L193.54,127.3L192.39,126.89L189.6,129.4L189.44,131.1L188.32,131.22L189.52,132.25L189.31,133.34L188.47,132.88L187.42,135.15L189.11,137.27L188.18,138.58L189.6,139.34L189.48,140.34L185.42,141.97L185.3,142.89L184.09,141.59L182.7,142.5L182.9,141.25L181.96,142.15L178.31,140.87L176.68,139.2L177.54,138.55L177.47,136.75L174.86,135.67L172.8,132.54L171.87,133.08L171.09,131.87L170.34,132.35L167.37,130.38L167.01,126.78L167.85,126.17L166.74,125.91L166.64,124.81L164.32,124.62L162.79,121.56L161.43,124.09L159.24,124.27L154.09,111.29L148.21,108.62L149.76,106.95L149.03,105.53L154.85,101.71L153.37,100.2L153.7,98.66Z",
    map_10: "M153.7,98.66L150.7,101.84L147.3,103.23L146.66,104.68L146.57,104L139.6,102.08L139.22,100.79L137.79,100.33L136.18,101.33L131.24,100.46L130.69,97.62L131.46,97.07L130.67,96.19L131.62,93.44L130.05,95.05L125.71,94.99L117.8,90.38L115.28,90L114.98,90.7L112.79,88.34L111.73,88.37L112.22,85.99L110.37,82.7L111.5,78.49L110.46,76.51L110.88,72L109.56,69.85L109.15,65.55L107.43,62.74L108.31,58.42L111.49,58.18L113.58,54.44L119.14,52.3L120.44,47L120.9,47.84L123.58,48.09L127.67,47.42L130.87,51.49L134.68,53.29L140.18,52.62L141.8,54.63L141.35,57.12L144.63,59L145.24,62.93L150.97,65.99L154.71,70.24L156.83,70.26L159.68,74.28L160.41,77.86L163.16,76.67L168.13,81.48L168.54,83.29L169.78,83.64L170.57,84.99L169.67,86.52L163.25,86.44L160.5,89.47L157.84,90.05L156.4,91.78L154.85,92.44L152.51,91.52L152.51,93.06L154.45,95.01L154.7,97.29L153.7,98.66Z",
    map_11: "M295.35,260.78L302.22,260.65L304.53,258.54L306.72,261.68L307.39,260.5L308.01,261.1L308.15,260.32L310.12,259.98L310.74,261.25L310.12,262.3L313.85,265.59L314.37,263.79L316.91,263.44L319.46,260.71L320.18,262.25L321.03,262.05L321.46,263.87L322.29,263.88L324.68,262.51L324.98,263.68L328.51,260.76L332.89,260.8L333.98,259.22L335.62,259.64L335.33,257.53L336.96,254.8L339.58,255.25L340.92,256.62L342.74,255.69L344.16,257.16L344.32,259.2L347.61,259.64L347.03,262.05L350.14,263.93L351.24,260.92L352.99,259.28L354.27,260.06L356.56,258.96L356.87,259.99L358.7,260.4L359.13,258.4L361.36,259.13L361.92,254.75L363.37,253.52L363.16,250.73L364.36,249.41L365.84,249.42L366.45,246.73L369.21,247.3L369.38,245.28L370.46,245.55L371.2,244.29L374.99,246.26L374.79,249.22L378.51,253.3L375.63,255.92L377.16,256.81L377.33,259.38L376.18,260.25L374.88,259.84L375.47,263.15L374.1,265.52L372.23,266.45L373.23,268.32L371.19,268.08L371.66,268.79L370.82,269.66L369.65,269L369.88,270.63L368.58,271.77L364.78,270.58L365.84,272.22L364.98,274.79L362.94,273.99L363.51,274.96L362.72,275.46L358.47,273.75L358.33,274.91L356.57,274.73L356.55,277.62L350.1,278.75L347.41,283L344.49,281.85L344.59,280.11L342.53,279.72L342.6,281.59L339.35,282L339.9,283.87L338.77,287.59L340.25,288.85L342.52,288.57L345.78,291.62L351.76,291.82L349.95,292.91L349.56,295.94L353.31,298.02L353.94,299.98L355.88,300.01L356.66,301.82L355.67,302.1L356.02,303.05L357.51,303.59L357.23,305.03L358.33,305.9L356.51,306.27L356.69,307.26L354.5,307.14L351.12,304.7L349.71,305.4L347.03,302.6L343.91,301.85L342.54,300.23L340.97,302.39L342.23,303.89L341.27,306.67L342.32,307.84L340.02,312.23L337.38,311.77L337.89,310.01L338.68,310.02L338.15,309.33L335.91,310.58L330,308.46L327.54,311.56L324.54,309.42L323.01,310.03L325.37,305.73L324.2,301.93L321.53,303.14L314.45,303.13L312.58,304.5L310.04,304.8L304.71,301.11L306.16,298.79L308.9,297.97L308.85,296.8L311.54,294.2L311.89,292.71L311.55,291.98L308.7,291.95L308.19,291L307.41,292.2L307.02,289.77L305.29,288.7L305.97,284.84L304.05,283.96L304.8,278.76L303.54,278.96L303.46,280.16L300.48,279.43L299.01,274.98L296.51,274.13L295.55,270.87L292.1,269.34L293.55,266.03L293.07,265.08L294.32,263.88L293.39,261.3L295.35,260.78Z",
    map_12: "M183.93,433.97L179.07,433.24L175.66,434.42L174.85,435.78L175.62,435.95L175.24,437.73L176.77,439.25L174.94,439.85L174.94,442.45L173.89,442.63L175.59,446.03L176.49,445.92L177.53,447.46L176.7,448.05L177.32,450.08L175.92,452.51L172.15,451.93L170.66,450.69L170.21,451.2L170.04,453.12L171.94,453.77L171.6,456.49L170.36,456.96L170,460.64L172.04,463.21L173.96,463L173.37,464.59L172.28,464.78L173.31,467.8L176.24,468.41L177.3,465.83L179.71,465.95L179.68,467.06L181.16,467.19L181.62,468.75L181.83,465.99L183.78,467.37L183.8,469.57L182.11,469.2L180.95,470.11L181.84,470.53L180.56,472.15L182.5,473.24L181.88,475.01L180.97,474.69L180.32,472.12L177.35,472.81L176.73,471.77L174.91,471.55L174.84,469.15L172.44,469.43L174,471.31L173.19,472.17L175.14,474.12L173.9,476.44L174.34,477.81L177.59,477.56L176.81,475.78L177.78,474.28L177.74,475.41L178.95,474.75L179.15,475.85L182.39,475.93L183.37,479.09L184.21,478.89L184.17,477.76L185.96,478.07L186.58,477.11L187.57,478.01L187.77,476.81L189.91,475.4L189.66,473.98L191.47,474.66L192.72,473.64L192.16,476.12L193.89,474.87L195.24,475.79L194.63,479.92L196.09,479.72L196.65,481.51L200.41,481.06L199.98,486.25L202.84,487.54L203.57,486.78L203.75,489.53L200.62,494.04L201.67,495.38L199.59,494.14L198.8,495.77L197.65,495.77L197.27,497.67L193.9,495.96L193.18,496.82L192.19,495.76L191.46,496.29L191.55,495.25L189.9,495.55L188.15,499.48L185.52,499.61L185.9,505.32L183.3,507.58L183.21,508.78L188.07,509.47L188.82,510.81L186.93,514.06L183.68,514.26L182.24,517.64L179.05,516.84L176.46,517.63L176.59,518.65L174.63,516.84L172.67,517.24L171.49,521.25L166.62,520.83L165.46,519.05L163.79,519.75L163.43,517.93L162.14,518.32L159.31,515.65L158.05,516.07L157.89,513.5L155.84,514.45L153.09,513.78L152.05,511.42L149.49,511.02L145.23,507.05L144.01,504.74L144.92,502.93L143.55,503.75L142.27,502.06L143.3,501.15L142.34,500.62L141.34,501.61L140.19,499.57L138.88,498.95L138.07,499.65L137.27,497.13L135.03,497.87L134.3,497.07L131.5,485.69L131.33,484L132.31,485.04L131.72,484.12L132.45,483.75L131.4,483.57L130.92,480.19L132.6,479.93L131.26,478.79L130.66,479.54L130.23,475.6L128.17,472.97L126.66,467.38L128.17,467.91L126.28,466.47L125.35,462.27L125.75,463.63L126.52,463.25L125.34,461.52L124.43,462.32L124.06,460.83L126.29,460.33L123.87,460.5L123.46,458.21L120.49,456.58L122.93,455.2L120.67,455.72L120.22,454.71L123.56,453.06L124.45,448.86L123.25,447.38L124.82,446.67L123.22,442.71L123.2,439.4L120.26,439.16L122.83,436.73L123.89,437.68L125.25,436.99L125.71,435.73L125.03,435.27L126.13,435.3L125.75,434.43L127.22,431.62L125.36,431.24L126.55,430.21L127.57,430.68L128.01,428.65L126.3,426.79L124.73,427.09L124.48,425.32L125.32,424.56L124.03,422.32L123.29,422.56L123.59,421.48L124.3,422.33L124.63,421.29L125.63,421.82L127.35,419.14L128.73,419.69L129.15,421.33L131.32,420.27L130.77,419.6L131.47,418.04L135.68,416.94L135.95,413.5L138.86,413.37L141.4,415.51L142.38,414.84L142.42,413.24L145.84,412.6L146.31,413.41L147.75,413.04L147.85,412.15L149.64,413.14L149.69,410.02L148.82,408.88L149.42,407.04L147.9,404.6L148.96,402.7L151.87,404.8L153.66,403.9L154.48,405.78L158.03,404.93L159.04,406.32L158.98,405.28L160.32,404.76L162.88,406.02L163.38,404.86L161.99,403.6L162.71,402.52L161.95,400.33L164.89,399L165.53,397.02L166.38,398.13L167.22,396.83L168.64,398.61L169.05,396.69L170.46,395.66L169.57,394.22L170.73,394.82L172.85,393.88L173.55,388.45L176.44,389.09L178.88,383.94L181.3,383.16L182.05,384.34L181.16,385.96L183.08,386.93L184.09,385.97L185.65,386.56L185.54,390.46L184.58,391.07L186.59,392.77L183.93,396.15L184.89,397.24L182.95,398.21L182.61,400.57L187.24,402.24L183.92,403.67L184.26,404.78L182.83,404.82L182.89,406.72L181.51,407.85L181.13,409.02L183.04,410.18L183.73,412.12L182.31,418.01L183.09,418.74L182.19,419.05L183.21,419.44L182.93,420.7L181.72,421.02L182.17,421.97L178.81,422.97L180.15,424.46L185.32,425.78L183.52,427.36L183.93,433.97Z",
    map_13: "M147.16,519.22L143.17,515.09L143.1,513.57L140.97,512.95L134.63,497.76L137.27,497.13L138.07,499.65L138.88,498.95L140.19,499.57L141.34,501.61L142.34,500.62L143.3,501.15L142.27,502.06L143.55,503.75L144.92,502.93L144.01,504.74L145.23,507.05L149.49,511.02L152.05,511.42L153.09,513.78L155.84,514.45L157.89,513.5L158.05,516.07L159.31,515.65L162.14,518.32L163.43,517.93L163.99,520.27L160.1,521.67L160.38,523.78L163.56,524.3L165.94,525.94L164.36,529.4L169.46,528.86L168.71,529.84L170.23,532.36L168.8,532.66L168,534.45L171.18,535.79L172.67,537.62L172.22,540.47L170.95,540.53L171.24,547.2L174.07,548.94L177.65,546.16L178.75,546.27L179.84,548.95L179.34,550.88L178.13,551.2L179.5,553.86L178.44,555.26L179,557.39L177.54,561.16L179.32,561.88L181,561.28L182.04,563.26L179.64,567.41L179.36,570.39L177.14,573.04L179.16,575.93L177.59,578.57L179.65,582.6L178.18,583.49L178.62,584.61L176.23,587.72L165.99,575.5L162.04,564.94L160.29,553.93L156.99,542.53L156.47,542L156.38,543.11L153.99,537.27L150.93,525.98L148.73,523.58L147.16,519.22Z",
    map_14: "M121.21,312.6L120.38,311.31L121.86,309.94L120.97,309.74L120.79,307.39L119.91,307.21L119.84,304.42L122.06,305.2L123.61,303.77L122.06,302.06L120.79,303.21L120.09,301.44L119.19,301.78L119.34,300.76L121.36,301.26L123.45,298.7L125.61,298.83L127.44,294.4L126.93,293.08L125.26,292.9L124.54,290.29L125.81,289.3L128.07,289.77L132.42,287.29L131.59,286.08L128.75,285.78L128.22,284.88L129.96,282.27L136.05,278.87L135.41,274.09L137.05,270.83L135.22,266.16L132.62,265.81L134.53,262.1L131.84,261.17L133.79,257.9L133.03,257.61L133.46,255.41L135.11,258.19L137.2,257.17L137.6,255.87L134.39,255.47L134.09,251.95L138.01,254.13L139.42,253.53L140.17,250.39L143.72,250.46L143.12,253.17L142.35,251.77L142.08,253.53L144.97,254.05L142.8,255.02L141.29,253.19L141.9,254.58L140.78,255.56L141.24,256.89L148.04,256.89L148.63,257.58L151.35,256.05L152.83,256.75L152.63,259.1L154.29,259.94L154.13,262.26L152.98,263.01L151.93,261.88L150.79,263.6L152.58,266.56L151.04,268.63L152.64,270L150.23,272.07L149.65,270.77L148.06,271.61L146.77,270.46L145.69,273.04L148.03,275.46L149.99,276.01L150.27,273.38L151.71,274.37L155.36,272.8L155.07,270.94L158.33,269.53L158.12,267.31L159.28,264.78L159.83,267.09L162.06,266.34L163.49,267.23L164.66,266.85L165.56,268.17L168.73,265.72L168.88,268L170.74,269.03L172.58,268.82L173.37,267.26L171.45,264.56L171,260.7L172.83,260.51L172.86,261.64L173.98,262.1L175.39,260.86L175.56,258.52L173.77,256.17L171.57,256.32L170.77,254.82L173.51,253.89L172.08,252.24L172.26,250.64L181.66,248.87L182.25,247.44L180.97,245.86L181.19,243.02L179.74,242.8L178.01,245.05L175.87,244.49L173.38,245.78L172.48,244.84L170.18,245.04L166.35,242.42L164.84,236.86L166.76,233.85L170.87,232.14L172.53,229.22L179.46,225.64L180.72,224.01L182.54,223.85L182.45,223.01L189.63,220.03L191.31,217.79L192.96,217.05L194.51,217.55L194.89,215.31L196.39,215.27L196.34,214.44L197.87,214.69L199.46,213.61L203.64,215.95L206.42,215.09L208.21,215.79L210.75,217.86L211.59,217.42L211.34,219.64L212.85,221.07L212.62,222.02L214.07,222.25L213.14,223.9L214.07,224.84L212.46,226.58L211.42,226.31L211.72,228.08L210.67,228.43L211.76,229.55L208.85,234.01L209.16,235.18L208.27,234.6L206.99,236.25L208.07,238.76L205.16,239.94L201.16,239.95L200.93,241.67L198.66,243.6L201.32,248.74L199.27,249.45L199.17,251.34L196.11,253.93L198.06,257.98L197.16,260.93L199.75,263.66L199.18,264.9L200.24,265.96L202.37,263.52L205.52,266.52L207.33,266.41L207.8,267.82L211.09,264.27L211.35,262.45L209.87,261.82L211.06,261.45L209.48,258.58L207.07,259.33L207.6,255.05L204.71,252.09L205.03,250.09L203.66,246.12L201.06,245.68L202.85,245.16L202.91,243.96L203.81,244.24L203.53,243.36L204.97,242.46L206.12,242.75L205.33,243.52L207.31,244.11L206.96,242.96L208.19,242.61L206.6,241.46L208.65,242.59L208.48,241.19L209.57,240.04L210.61,240.23L209.89,242.33L210.95,242.49L211.22,243.83L209.36,243.52L207.66,245.22L208.83,246.62L209.4,244.39L210.87,244.33L211.04,245.61L209.31,246.28L209.4,248.06L210.81,246.88L211.61,247.38L211.56,245.74L212.74,246.78L212.1,248.44L213.26,247.45L213.83,248.95L215.13,248.28L216.13,248.98L216.35,248.08L216.84,248.9L217.54,248.27L216.78,246.33L218.15,247.01L218.02,245.83L216.47,245.7L217.18,244.48L218.26,245.98L220.5,245.64L218.83,247.9L219.17,249.04L220.44,248.69L220.81,249.66L222.28,247.79L222.77,248.62L227.62,249.34L227.42,246.56L230.25,245.88L230.69,244.41L232.66,244.46L235.06,242.69L236.05,243.43L236.1,245.63L237.72,246.42L238.18,247.81L236.78,248.48L236.72,250.13L235.53,250.1L236.14,251.24L237.41,251.12L237.13,250.13L238.5,249.66L239.44,250.38L238.21,250.69L238.71,251.58L240.38,251.18L239.12,249.26L241.58,249.77L241.36,248.18L242.83,250.22L244.73,250.06L243.34,248.73L245.14,248.36L245.69,249.09L245.86,247.43L246.63,247.37L247.14,247.99L245.22,252.42L250.32,252.26L251.28,253.44L251.9,252.14L252.73,252.52L253.9,247.98L254.64,248.77L255.4,247.93L257.9,249.79L257.78,248.51L259.44,247.36L260.43,248.16L260.22,250.11L261.17,249.72L265.67,251.63L266.43,254.47L271.38,255.35L271,256.44L272.14,256.18L271.75,257.83L272.92,258.03L272.94,258.99L275.1,259.37L275.31,257.21L277.33,258.28L279.73,257.3L282.3,259.33L282.38,260.27L280.81,260.18L280.64,263.64L281.7,264.4L281.18,268L279.71,268.68L281.74,269.82L281.36,271.13L282.51,272.13L279.66,273.28L279.19,274.63L277.29,274.93L277.08,275.73L271.6,275.22L271.28,274.36L268.33,275.01L265.93,273.96L263.98,275.24L261.19,272.91L260.19,273.36L261.67,277.01L259.48,279.69L260.14,281.25L262.45,280.16L264.27,281.24L265.93,280.82L267.09,283.24L269.31,283.52L270.97,285.03L270.29,288.66L266.33,289.95L266.37,292.38L263.19,294.06L263.07,298.29L261.5,298.58L260.45,300.7L258.47,300.84L256.25,302.76L254.89,301.1L254.53,301.95L253.04,302.56L252.05,301.83L250.92,302.77L251,305.72L249.24,307.04L249.17,308.98L248.33,309.34L248.86,310.41L247.34,309.22L245.5,312.52L245.75,315.57L245.36,316.73L243.79,317.06L243.86,322.27L242.33,325.2L239.3,323.77L237.8,324.21L237.17,321.29L235.11,319.3L233.77,319.02L228.7,321.23L225.29,319.66L221.65,320.84L220.75,318.23L215.92,317.23L215.69,318.71L210.04,319.88L210.39,321.99L201.05,321.71L200.91,319.7L200.11,319.36L199.76,320.17L196.41,320.57L196.19,321.78L191.9,324.03L189.3,323.52L188.16,324.44L187.23,323.83L185.62,324.47L184.23,323.35L183.48,324.22L182.3,320.9L185.72,320.9L183.19,316.3L179.57,316.53L178.28,317.86L175.7,317.3L172.57,319.73L170.6,319.8L170.39,322.44L167.49,324.99L168.09,326.14L167.39,327.95L164.93,327.84L162.79,330.27L158.92,330.1L157.9,328.51L158.99,328.04L157.61,324.3L141.23,323.55L139.12,322.37L138.21,320.45L135.3,319.13L131.81,319.21L128.04,317.24L128.33,313.52L126.62,311.06L123.93,312.96L121.21,312.6Z",
    map_15: "M242.49,325.11L242.03,326.79L238.78,328.29L238.43,329.32L238.16,331.57L238.81,333.21L240.23,333.14L241.03,338.22L239.23,339.46L241.85,339.73L241.75,345.32L237.4,347.01L237.6,348.98L239.54,349.08L240.44,350.55L239.96,353.26L237.83,353.23L239.53,354.51L237.72,356.02L239.17,356.45L238.83,355.66L239.91,355.31L242.67,358.05L242.6,359.73L245.53,360.6L246.88,362.6L244.94,363.39L245.96,364.61L244.22,366.2L241.52,365.7L240.69,363.98L237.33,367.3L235.26,372.24L236.72,375.78L234.79,376.81L235.26,377.65L232.27,378.47L228.31,375.39L229.15,374.83L229.06,371.55L227.67,371.3L227.64,369.9L229.17,368.68L229.77,363.93L226.28,360.06L223.57,360.44L222.67,361.91L221.76,360.97L220.42,362L216.07,359.75L215.18,362.78L213.46,361.34L210.81,360.92L211.08,358.96L209.03,358.82L208.78,356.77L204.28,355.58L202.24,356.07L198.81,353.67L198.51,354.94L199.89,356.32L198.06,358.69L198.79,362.85L196.43,363.75L196.45,367.17L194.11,367.21L192.09,365.17L190.44,365.96L191.28,366.66L190.57,366.9L190.1,370.29L189.32,370.05L188.52,371.46L191.97,375.61L190.01,375.91L189.06,378.39L188.06,378.56L187.97,381.06L185.54,381.14L185.61,382.62L184.59,382.79L185.27,383.24L184.21,383.56L184.09,385.13L185.03,385.96L183.08,386.93L181.16,385.96L182.05,384.34L180.32,383.03L178.88,383.94L176.44,389.09L173.55,388.45L172.85,393.88L170.73,394.82L169.57,394.22L170.46,395.66L169.05,396.69L168.64,398.61L167.22,396.83L166.38,398.13L165.53,397.02L164.89,399L161.95,400.33L162.71,402.52L161.99,403.6L163.38,404.86L162.88,406.02L160.32,404.76L158.98,405.28L159.04,406.32L158.03,404.93L154.48,405.78L153.66,403.9L151.87,404.8L148.96,402.7L147.9,404.6L149.42,407.04L148.82,408.88L149.69,410.02L149.64,413.14L147.85,412.15L147.75,413.04L146.31,413.41L145.84,412.6L142.42,413.24L142.38,414.84L141.4,415.51L138.86,413.37L135.95,413.5L135.68,416.94L131.47,418.04L130.77,419.6L131.32,420.27L129.15,421.33L128.73,419.69L127.35,419.14L125.63,421.82L124.63,421.29L124.3,422.33L123.5,421.6L123.45,422.86L124.03,422.32L125.32,424.56L124.48,425.32L124.73,427.09L126.3,426.79L128.02,428.39L127.57,430.68L126.55,430.21L125.36,431.24L127.22,431.62L125.75,434.43L126.13,435.3L125.03,435.27L125.71,435.73L125.25,436.99L123.89,437.68L122.49,436.89L120.95,438.03L120.73,439.58L118.95,440.4L115.92,436.51L115.31,437.72L112.55,437.96L110.95,434.29L109.05,432.98L106.91,425.17L107.46,424.43L105.92,422.15L105.93,420.4L107.4,420L106.22,420.01L105.64,417.52L105.28,413.38L106.16,413.35L104.78,411.4L105.79,410.86L103.77,406.7L104.5,405.9L103.16,404.3L102.55,397.87L100.13,391.91L100.76,391.22L99.84,391.2L99.54,388.19L98.72,387.77L99.28,385.84L97.98,382.95L98.71,381.3L97.52,379.71L97.47,376L96.03,373.17L96.27,365.41L93.96,357.68L94.82,355.68L93.37,355.16L94.11,352.72L95.75,353.99L94.48,350.29L97.39,347.43L99.23,347.64L99.29,349.28L100.11,349.2L100.63,350.47L103.17,350.88L105.6,347.75L107.76,347.87L107.1,344.07L109.14,341.05L107.32,338.99L108.36,337.54L112.23,340.6L113.76,340.51L115.6,339.38L115.15,337.97L117.52,336.99L116.72,332.14L114.79,330.88L115.07,330.1L114.19,330.43L110.79,328.42L111.69,329.13L115.16,328.34L115.34,326.42L117.51,325.81L117.77,323.89L119.35,323.38L119.79,322.15L123.39,322.44L124.74,320.94L119.75,320.55L115.88,321.82L114.52,319.22L116.35,318.82L116.55,317.71L114.95,315.23L121.07,312.62L123.93,312.96L126.62,311.06L128.33,313.52L128.04,317.24L129.48,318.44L133.42,319.54L135.3,319.13L138.21,320.45L139.12,322.37L141.23,323.55L157.61,324.3L158.99,328.04L157.9,328.51L158.3,329.28L161.02,330.31L164.21,329.51L164.93,327.84L167.39,327.95L168.09,326.14L167.49,324.99L170.39,322.44L170.6,319.8L172.57,319.73L175.7,317.3L178.28,317.86L179.57,316.53L183.19,316.3L185.72,320.9L182.3,320.9L183.48,324.22L184.23,323.35L185.62,324.47L187.23,323.83L188.16,324.44L189.3,323.52L191.9,324.03L196.19,321.78L196.41,320.57L199.76,320.17L200.11,319.36L200.91,319.7L201.05,321.71L210.39,321.99L210.04,319.88L215.69,318.71L215.92,317.23L220.75,318.23L221.65,320.84L225.29,319.66L228.7,321.23L233.77,319.02L235.11,319.3L237.17,321.29L237.8,324.21L239.3,323.77L242.49,325.11Z",
    map_16: "M503.14,242.1L501.34,247.01L504.31,248.62L503.81,252.53L496.51,264.67L493.43,274.45L489.58,272.48L487.02,272.88L485.73,271.29L483.52,271.15L481.43,272.52L478.44,269.48L476.61,270.96L476.47,269.72L473.74,270.3L473.34,269.36L471.43,269.12L472.68,264.52L471.88,263.31L473.68,259.7L473.29,257.13L473.97,255.28L475.47,255.2L476.51,250.92L478.12,249.37L479.39,246.01L480.7,245.08L481.27,246.39L483.18,247.24L486.92,241.94L486.23,240.43L490.67,239.34L492.18,240.6L493.87,240.19L494.61,241.33L496.29,241.38L501.12,237.3L500.82,241.03L503.14,242.1Z",
    map_17: "M461.2,250.63L454.51,247.46L448.64,247.78L446.69,248.74L445.97,247.74L443.51,248.5L439.67,247.08L424.42,248.34L415.64,245.18L413.08,245.28L413.7,240.36L416.52,238.97L414.24,236.24L418.3,231.95L425.05,230.79L425.94,233.09L426.93,231.94L427.79,233.13L428.93,232.09L430.07,231.96L430.63,232.94L432.58,232.07L435.3,233.36L434.87,234.71L438.34,234.02L438.91,236.67L440.93,234.49L443.73,233.87L445.46,230.44L446.5,230.66L446.11,232.32L447.19,233.02L448.34,229.92L449.98,228.72L451.96,231.12L458.94,229.58L456.24,232.23L457.51,233.11L456.17,234.89L456.48,237.84L460.45,236.22L463.96,239.99L465.46,239.35L463.94,241.93L467.89,244.58L467.52,246.26L468.23,246.82L464.86,248.91L463.06,248.45L461.2,250.63Z",
    map_18: "M478.13,270.37L480.12,277.85L479.55,283.77L478.65,284.45L479.16,288.79L477.46,291.41L474.49,290.35L474.93,293.13L473.69,295.39L474.55,299.9L473.92,300.99L475.39,303.1L475.71,306.45L474.56,306.85L474.71,308.11L472.84,307.6L473.06,309.64L472.14,309.57L471.82,312.14L471.02,311.09L470.09,312.62L469.43,310.46L466.42,308.59L466.03,311.19L464.97,311.98L463.8,309.06L464.72,308.24L462.97,297.52L461.64,293.67L460.27,292.84L459.94,286.79L460.84,286.71L458.11,275.13L459.44,273.18L458.81,266.33L461.14,266.31L461.4,268.34L462.24,268.53L463.52,266.46L464.66,266.31L464.93,264.68L466.15,264.24L467.51,260.93L468.77,263.84L470.23,263.11L472.32,263.57L471.43,269.12L473.34,269.36L473.74,270.3L476.47,269.72L476.61,270.96L478.13,270.37Z",
    map_19: "M512.72,210.2L513.49,217.4L510.3,222.15L511.59,223.41L511.3,229.02L512.47,229.73L509.58,232.84L510.03,236.01L507.99,237.77L507.28,239.95L504.82,241.67L502.08,241.91L500.82,241.03L501.12,237.3L496.29,241.38L494.61,241.33L493.87,240.19L492.18,240.6L490.67,239.34L489.83,240.12L487.43,239.98L486.23,240.43L486.92,241.94L483.18,247.24L481.27,246.39L480.38,242.4L478.09,240.24L485.03,234.13L484.68,232.99L486.27,231.86L486.68,234.91L490.1,232.8L490.48,227.99L492.5,224.61L493.75,224.25L494.43,221.32L495.74,219.83L496.38,221.86L499.19,217.7L503.26,216.44L506.97,212.42L509.48,212.57L512.72,210.2Z",
    map_20: "M369.43,319.65L368.06,320.73L366.96,320.18L367.53,321.07L363.11,321.56L359.34,324.69L357.4,328.62L359.84,335.95L357.93,335.94L356.56,337.07L360.04,336.37L360.5,337.42L358.64,337.7L360.6,338.16L355.74,341.55L355.02,344.03L356.11,344.06L356.3,345.36L351.68,348.35L348.54,352.98L336.08,356.93L329.65,360.3L321.1,367.15L318.82,370.45L317.03,368.73L315.72,369.4L316.84,370.83L312.66,371.86L312.65,373.62L310.89,374.62L310.48,376.32L306.38,377.01L305.18,375.86L302.26,375.54L300.74,371.74L299.86,372.22L300.42,373.88L297.59,369.05L295.96,371.79L294.62,370.5L294.55,372.99L293.68,371.98L291.81,372.09L293.73,374.73L287.01,379.67L286.69,381L287.73,381.3L286.39,383.01L287.13,384.43L284.28,384.86L283.94,383.72L282.36,383.18L281.76,385.36L279.24,387.43L278.22,385.85L278.81,384.96L277.45,384.19L277.79,383.22L276.31,381.23L274.21,383.79L274.64,384.54L273.72,385.65L274.8,385.87L273.19,388.09L274.29,389.22L273.63,391.21L272.3,391.33L272.47,392.3L267.96,390.87L260.27,395.76L256.06,395.94L257.77,392.57L258.72,386.84L262.75,385.1L263.07,383.77L266.51,380.8L265.51,378.9L266.52,379.4L267.05,378.06L268.95,377.72L270.49,376.13L270.67,374.11L271.94,373.73L271.43,370.11L270.46,369.39L270.89,365.51L270.11,364.58L270.9,363.64L269.7,363.41L269.2,361.69L267.9,361.85L268.6,356.19L264.85,353.86L264.88,351.37L266.44,349.79L267.94,351.63L268.56,350.83L271.77,351.87L273.8,355.24L275.71,353.77L278.59,354.63L278.36,356.42L280.47,355.3L280.51,351.96L274.75,350.63L275.46,343.27L273.5,340.69L274.31,339.17L273.92,334.06L276.08,335.19L279.05,330.98L279.3,328.71L285.26,328.18L288.3,329.68L291.05,326.31L291.09,324.17L293.56,324.7L292.29,321.74L293.15,319.38L295.08,319.12L293.87,318.13L295.07,316.79L294.76,315.97L296.94,314.74L297.23,313.39L296.02,312.33L295.97,310.91L297.23,310.28L296.43,309.59L298.08,307.08L304.68,304.16L305.43,302.26L304.23,301.17L307.06,302.07L307.5,303.52L310.04,304.8L312.58,304.5L314.45,303.13L321.53,303.14L324.2,301.93L325.37,305.73L323.01,310.03L324.54,309.42L327.54,311.56L330,308.46L335.91,310.58L338.15,309.33L338.68,310.02L337.89,310.01L337.38,311.77L340.02,312.23L342.32,307.84L341.27,306.67L342.23,303.89L340.97,302.39L342.54,300.23L343.91,301.85L347.03,302.6L349.71,305.4L351.12,304.7L355.26,307.23L355.12,308.67L356.51,308.46L357.29,309.64L360.73,310.73L360.97,314.26L362.16,314.36L363.39,312.31L364.77,312.47L365.62,315.59L368.65,316.36L369.43,319.65Z",
    map_21: "M170.34,132.35L171.76,134.65L170.92,135.25L170.14,134.52L168.86,135.57L169.74,136.73L172.06,137.09L173,138.54L172.55,141.82L173.25,142.79L172.5,143.4L171.49,141.88L169.49,142.15L169.46,143.68L166.15,145.4L167.88,146.89L165.91,148.95L164.4,148.58L163.62,146.54L163.24,148.04L162.05,147.6L162.09,148.4L159.75,147.53L160.71,148.47L159.07,152.04L160.48,153.25L156.74,155.66L153.72,155.69L151.72,154.11L150.5,154.45L150.34,155.52L147.94,155.77L145.63,154.45L143.27,157.21L142.76,159.41L141.78,159.61L140.29,157.37L141.66,155.57L141.1,154L140,155.06L139.04,154.46L138.94,152.28L137.17,153.46L135.8,151.58L133.66,150.7L131.7,151.12L130.65,152.48L116.54,150.58L118.16,146.8L116.45,143.23L118.06,141.8L117.37,140.68L117.97,141.21L118.88,139.81L119.84,140.05L120.98,137.77L123.92,135.73L123.48,134.99L125.5,132.51L128.81,130.43L129.1,129.3L131.72,128.46L130.45,127.65L129.32,128.68L128.22,127.64L128.87,123.01L130.76,121.08L129.46,120.18L130.12,118.99L127.62,115.85L129.09,115.1L129.95,112.56L133.91,111.11L135.71,108.97L137.42,109.55L142.05,108.19L144.22,105.45L143.49,103.36L146.84,104.62L147.3,103.23L150.7,101.84L153.7,98.66L153.37,100.2L154.85,101.71L149.03,105.53L149.76,106.95L148.21,108.62L154.09,111.29L159.24,124.27L161.43,124.09L162.79,121.56L164.32,124.62L166.64,124.81L166.74,125.91L167.85,126.17L167.01,126.78L167.37,130.38L170.34,132.35Z",
    map_22: "M128.41,151.68L129.05,153.25L127.4,154.82L127.53,155.7L130.01,155.53L129.35,158.89L130.12,160.22L128.58,161.64L129.82,164.08L134.54,162.59L135.99,163.37L136.47,165.03L138.08,164.93L138.79,166.04L140.69,165.31L141.5,165.99L143.28,164.82L143.2,165.98L144.71,165.45L144.06,167.86L145.42,170.4L146.85,170.52L146.02,172.22L147.66,178.5L151.8,181.67L151.65,182.68L154.48,183.47L157.65,187.78L156.44,187.42L154.73,189.01L155.63,189.88L156.5,189.21L154.49,192.25L155.45,193.63L157.55,193.55L159.13,194.78L160.07,194.11L158.78,191.05L159.7,190.29L159.22,189.71L162.19,190.31L162.44,189.38L161.45,188.87L162.54,188.32L161.12,187.31L164.71,187.9L165.18,188.76L164.27,189.03L165.18,189.61L164.4,189.97L165.94,190.12L166.09,191.49L168.2,190.53L168.04,188.97L172,186.39L173.82,188.02L172.5,197.37L173.97,197.83L174.42,196.12L175.97,196.21L175.24,194.55L180.02,194.96L180.85,200.5L182.51,201.76L182.55,203.03L185.98,204.25L185.52,205.01L187.03,207.03L184.08,209.65L186.18,210.75L186.99,210.08L187.6,211.06L188.35,210.59L182.36,213.74L182.84,216.16L183.77,214.13L185.83,214.4L188.52,212.32L193.77,213.59L195.25,211.98L197.99,212.61L196.65,213.41L196.39,215.27L194.89,215.31L194.75,217.38L191.31,217.79L189.63,220.03L176.6,226.54L172.53,229.22L170.66,232.32L166.76,233.85L164.84,236.86L166.35,242.42L170.18,245.04L172.48,244.84L173.38,245.78L175.87,244.49L178.01,245.05L179.74,242.8L181.19,243.02L180.97,245.86L182.25,247.44L181.66,248.87L172.26,250.64L172.08,252.24L173.51,253.89L170.77,254.82L171.57,256.32L173.77,256.17L175.56,258.52L175.39,260.86L173.98,262.1L172.86,261.64L172.83,260.51L171,260.7L171.45,264.56L173.37,267.26L172.58,268.82L170.74,269.03L168.88,268L168.73,265.72L165.56,268.17L164.66,266.85L163.49,267.23L162.06,266.34L159.83,267.09L159.28,264.78L158.12,267.31L158.33,269.53L155.07,270.94L155.36,272.8L151.71,274.37L150.27,273.38L149.99,276.01L148.03,275.46L145.69,273.04L146.77,270.46L148.06,271.61L149.65,270.77L150.23,272.07L152.64,270L151.04,268.63L152.58,266.56L150.79,263.6L151.93,261.88L152.98,263.01L154.13,262.26L154.29,259.94L152.63,259.1L152.83,256.75L151.35,256.05L148.63,257.58L148.04,256.89L141.24,256.89L140.78,255.56L141.9,254.58L141.29,253.19L142.8,255.02L144.97,254.05L142.08,253.53L142.35,251.77L143.12,253.17L143.72,250.46L140.17,250.39L139.42,253.53L138.01,254.13L134.09,251.95L134.39,255.47L137.6,255.87L137.2,257.17L135.11,258.19L133.46,255.41L133.03,257.61L133.79,257.9L131.84,261.17L134.53,262.1L132.62,265.81L135.22,266.16L137.05,270.83L135.41,274.09L136.05,278.87L129.96,282.27L128.22,284.88L128.75,285.78L131.59,286.08L132.42,287.29L128.07,289.77L125.81,289.3L124.54,290.29L123.4,288.09L120.89,287.94L120.94,286.05L117.98,283.95L116.55,284.75L115.3,282.54L114.14,282.3L113.42,283.22L113.01,282.36L111.68,282.42L112.21,278.99L110.65,278.33L109.46,279.11L109.25,277.3L106.62,275.58L107.73,272.93L106.79,269.12L104.61,271.15L103.64,270.47L104.07,269.4L101.44,267.65L104.22,264.03L101.49,263.43L101.67,261.41L99.56,262.28L99.75,264.04L98.49,264.79L94.92,264.08L94.19,262.08L91.37,261.18L90.73,263.09L89.93,263.14L89.52,261.18L85.95,259.64L87.87,258.88L84.28,258.92L82.25,257.1L80.05,258.68L78.86,257.76L78.62,259.13L77.52,257.79L74.95,258.54L71.72,257.71L68.17,259.03L64.84,257.78L61.61,252.41L60.66,248.18L56.57,243.22L56.43,237.01L51.46,237.66L49.33,236.94L46.04,232.39L45.8,229.32L47.4,226.43L47.42,219.99L46.03,219.11L40.49,219.03L35.17,216.15L34.61,214.82L36.57,207.27L44.66,199.65L46.7,194.7L51.16,190.57L55.21,190.62L56.79,192.34L57.91,195.82L60.61,196.69L67.86,193.83L75.04,193.32L79.5,191.55L80.13,188.24L85.2,182.95L87.01,177.25L88.62,175.33L99.04,170.01L105.02,159.68L107.56,151.62L114.92,149.1L118.13,146.58L116.72,151.11L128.41,151.68Z",
    map_23: "M379.23,206.59L380.12,204.15L379.79,201.02L382.61,195.08L381.38,191.81L386.3,191.27L390.63,188.18L394.48,190.51L395.44,194.06L394.8,197.48L393.32,199.58L394.1,202.8L396.2,204.38L395.76,205.42L393.94,205.97L392.66,208.19L390.05,207.04L387.08,209.31L384.71,208.37L381.88,208.71L379.23,206.59Z",
    map_24: "M224.49,515.35L226.6,516.16L225.29,516.2L224.8,517.28L226.34,517.37L225.66,521.53L227.18,526.2L227.49,532.92L225.45,533.16L224.97,534.5L227.35,536.66L227.91,547.46L226.39,547.85L223.42,546.5L222.96,546.9L225.86,547.81L221.68,547.09L222.95,546.46L221.46,546.15L221.21,547.03L219.02,546.77L217.12,547.98L215.81,550.39L216.45,552.59L211.4,559.1L209.59,563.81L212.52,567.41L214.98,567.85L209.09,568.31L200.36,571.45L196.52,574.89L196.03,578.31L196.79,578.14L195.58,579.63L195.43,583.54L194.14,586.13L188.37,589.99L185.32,590.74L184.7,592L180.27,591.14L176.23,587.72L178.62,584.61L178.18,583.49L179.65,582.6L177.59,578.57L179.16,575.93L177.14,573.04L179.36,570.39L179.64,567.41L182.04,563.26L181,561.28L179.32,561.88L177.54,561.16L179,557.39L178.44,555.26L179.5,553.86L178.13,551.2L179.34,550.88L179.84,548.95L178.75,546.27L177.65,546.16L174.07,548.94L171.24,547.2L170.95,540.53L172.22,540.47L172.67,537.62L171.18,535.79L168,534.45L168.8,532.66L170.23,532.36L168.71,529.84L169.46,528.86L164.36,529.4L165.94,525.94L163.56,524.3L160.38,523.78L160.1,521.67L162.42,521.3L165.3,518.97L166.22,520.68L171.49,521.25L172.67,517.24L174.63,516.84L176.59,518.65L176.46,517.63L179.05,516.84L182.24,517.64L183.68,514.26L186.93,514.06L188.83,510.64L187.95,509.39L183.53,509.02L183.16,508.11L185.9,505.32L185.52,499.61L188.15,499.48L189.9,495.55L191.55,495.25L191.46,496.29L192.19,495.76L192.81,496.82L194.36,496.12L197.27,497.67L196.89,499.22L201.72,500.38L202.64,498.62L202.09,498.12L203.45,498.78L205.2,494.31L204.71,493.03L206.07,492.59L206.23,491.55L209.61,491.02L209.58,492.27L211.44,491.13L214.62,492.51L215.77,490.09L217.51,490.7L219.61,488.93L219.62,486.88L218.75,486.55L219.54,486.17L221.75,486.38L222.3,487.62L224.22,486.96L225.63,488.71L226.36,488.04L225.2,487.16L229,485.87L229.89,483.22L231.05,482.87L230.19,482.03L231.74,481.88L231.65,483L234.65,483.47L235.17,481.55L236.59,486.98L233.2,503.4L227.36,512.02L227.18,513.95L225.54,514.61L225.77,513.02L223.87,512.8L225.32,514.93L224.49,515.35Z",
    map_25: "M184.66,386.3L184.21,383.56L185.27,383.24L184.59,382.79L185.61,382.62L185.54,381.14L187.97,381.06L188.06,378.56L189.06,378.39L190.01,375.91L191.97,375.61L188.52,371.46L189.32,370.05L190.1,370.29L190.57,366.9L191.28,366.66L190.44,365.96L192.09,365.17L194.11,367.21L196.45,367.17L196.43,363.75L198.79,362.85L198.06,358.69L199.89,356.32L198.51,354.94L199.15,353.6L202.24,356.07L204.28,355.58L208.78,356.77L209.03,358.82L211.08,358.96L210.81,360.92L213.46,361.34L215.18,362.78L216.07,359.75L220.42,362L221.76,360.97L222.67,361.91L223.57,360.44L226.28,360.06L229.77,363.93L229.17,368.68L227.64,369.9L227.67,371.3L229.06,371.55L228.72,376.06L232.27,378.47L235.26,377.65L236.65,380.29L239.37,379.48L242.06,381.64L244.95,386.96L243.96,388.62L245.14,388.73L245.62,387.37L246.33,389.4L248.41,388.68L247.86,390.44L249.5,396.29L251.87,395.02L257.85,396.01L263.01,394.19L263.68,395.17L259.73,397.46L256.97,404.61L254.91,404.33L253.88,405.72L252.29,405.68L251.94,407.48L249.4,408.41L247.15,408.12L245.8,411.65L242.1,410.99L242.33,410.28L240.73,409.4L238.61,411.84L237.66,410.62L237.13,411.05L237.01,412.78L241.18,413.65L240.78,416.95L237.75,415.2L237.25,415.98L235.18,411.97L233.69,411.29L230.48,414.06L230.15,414.95L231.6,415.94L229.24,419.58L226.12,417.64L219.64,420.61L216.65,421.04L215.56,427.69L212.07,427.42L210.31,428.27L210.03,430.22L208.57,429.29L207.51,431.87L204.57,430.36L203.49,431.21L200.91,430.69L198.07,431.66L197.6,433.62L194.96,435.82L193.61,434.32L193.11,435.2L190.98,434.46L189.33,435.08L183.93,433.97L183.52,427.36L185.32,425.78L180.15,424.46L178.81,422.97L182.17,421.97L181.72,421.02L182.93,420.7L183.21,419.44L182.19,419.05L183.09,418.74L182.31,418.01L183.73,412.12L183.04,410.18L181.13,409.02L181.51,407.85L182.89,406.72L182.83,404.82L184.26,404.78L183.92,403.67L187.16,402.75L185.32,401.04L183.76,401.39L182.61,400.57L182.95,398.21L184.89,397.24L183.93,396.15L186.59,392.77L184.58,391.07L185.6,390.15L185.65,386.56L184.66,386.3Z",
    map_26: "M457.63,261.26L458.37,263.58L457.25,266.38L458.81,266.33L459.55,268.45L458.29,276.92L457.47,278.36L456.17,276.72L454.6,278.48L452.48,276.84L452.9,281.8L450.47,283.4L449.16,285.94L450.23,289.58L446.26,292.63L444.85,291.55L443.04,286.21L441.85,287.58L442.54,290.07L441.33,289.58L439.95,283.32L437.73,279.39L438.56,277.77L437.58,276.82L438.93,276.5L439.99,271.77L441.86,271.85L441.8,269.25L445.69,269.87L447.11,266.71L448.86,268.52L448.64,266.44L450.47,266.83L451.57,268.61L451.95,264.54L453.14,264.76L453.4,263.45L455.6,263.67L456.49,262.88L456.25,260.8L457.63,261.26Z",
    map_27: "M185.3,142.89L186.43,142.37L191.89,145.81L189.23,148.63L187.67,152.86L189.33,157.05L192.09,156.39L192.22,158.9L193.79,159.68L199.36,154.75L202.26,155.86L203.91,159.1L209.93,161.61L206.71,164.49L209.25,165.77L209.92,167.76L211.16,167.46L212.09,168.42L212.41,167.35L214.21,168.19L214.65,170.24L217.27,171.72L218.46,171.39L219.42,173.7L221.88,173L222.02,173.9L224.16,173.85L226.18,172.97L226.5,174.76L227.57,173.91L229.49,176.48L230.62,176.08L231.46,174.11L232.63,174.26L239.87,179.58L239.61,177.4L240.52,176.89L244.79,180.25L247.05,180.84L247.07,181.61L252.74,183.6L254.89,188.04L256.73,187.37L257.64,189.12L265.31,193.68L266.81,192.23L268.73,192.35L275.87,197.33L280.35,196.44L281.22,200.88L289.36,201.83L291.73,204.32L293.21,203.45L293.41,201.33L297.25,201.49L301.55,203.58L302.02,204.75L303.3,204.64L302.89,205.94L304.44,207.35L303.61,209.21L305.1,209.09L304.55,209.58L305.47,210.11L305.68,213.19L306.98,213.17L307.85,214.34L309.28,213.85L309.21,216.43L310.25,215.97L312.41,218.51L309.76,219.07L306.2,218.24L305.59,220.2L302.89,220.71L302.89,222.08L307.89,223.61L307.55,226.2L304.8,226.43L304.75,227.55L307.9,231.16L314.52,233.59L316.41,236.59L315.02,236.43L314.96,237.48L313.77,237.61L310.85,236.27L310.08,237.91L306.44,236.76L306.11,238.43L303.22,239.95L301.16,241.88L301.68,242.47L292.59,247.26L291.97,250.49L292.67,253.87L296.1,258.75L295.35,260.78L293.39,261.3L294.32,263.88L293.07,265.08L293.55,266.03L292.57,268.84L289.6,272.97L284.94,273.88L281.36,271.13L281.74,269.82L279.83,268.9L279.96,268.2L280.88,268.6L281.7,265.51L281.63,263.89L280.64,263.64L280.81,260.18L282.38,260.27L281.68,258.4L280.55,258.48L279.73,257.3L277.33,258.28L275.53,257.19L275.1,259.37L272.94,258.99L272.92,258.03L271.75,257.83L272.14,256.18L271,256.44L271.38,255.35L266.43,254.47L265.67,251.63L261.17,249.72L260.22,250.11L260.43,248.16L259.44,247.36L257.78,248.51L257.9,249.79L255.4,247.93L254.64,248.77L253.9,247.98L252.73,252.52L251.9,252.14L251.28,253.44L250.32,252.26L245.22,252.42L247.14,247.99L246.63,247.37L245.86,247.43L245.69,249.09L245.14,248.36L243.34,248.73L244.73,250.06L242.83,250.22L241.36,248.18L241.58,249.77L239.12,249.26L240.38,251.18L238.71,251.58L238.21,250.69L239.44,250.38L238.5,249.66L237.13,250.13L237.41,251.12L235.48,250.88L235.53,250.1L236.72,250.13L236.78,248.48L238.18,247.81L237.72,246.42L236.1,245.63L236.05,243.43L235.06,242.69L232.66,244.46L230.69,244.41L230.25,245.88L227.42,246.56L227.62,249.34L222.77,248.62L222.28,247.79L220.81,249.66L220.44,248.69L219.17,249.04L218.83,247.9L220.5,245.64L218.26,245.98L217.18,244.48L216.47,245.7L218.02,245.83L218.15,247.01L216.78,246.33L217.29,248.69L216.35,248.08L216.13,248.98L215.13,248.28L214.17,249.09L213.26,247.45L212.1,248.44L212.74,246.78L211.92,245.81L211.2,246.22L211.61,247.38L210.81,246.88L209.4,248.06L209.31,246.28L211.04,245.61L210.87,244.33L209.4,244.39L208.83,246.62L207.66,245.22L209.36,243.52L211.22,243.83L210.95,242.49L209.89,242.33L210.31,240.06L208.61,240.94L208.65,242.59L206.6,241.46L208.19,242.61L206.96,242.96L207.31,244.11L205.33,243.52L206.12,242.75L204.97,242.46L203.53,243.36L203.81,244.24L202.91,243.96L202.85,245.16L201.06,245.68L203.66,246.12L205.03,250.09L204.71,252.09L207.6,255.05L207.07,259.33L209.48,258.58L211.06,261.45L209.87,261.82L211.35,262.45L211.09,264.27L207.8,267.82L207.33,266.41L205.52,266.52L202.65,263.59L200.64,265.88L199.42,265.26L199.75,263.66L197.1,260.75L198.06,257.98L196.11,253.93L199.17,251.34L199.27,249.45L201.32,248.74L198.66,243.6L200.93,241.67L201.16,239.95L205.16,239.94L208.07,238.76L206.99,236.25L208.27,234.6L209.16,235.18L208.85,234.01L211.76,229.55L210.67,228.43L211.72,228.08L211.42,226.31L212.46,226.58L214.07,224.84L213.14,223.9L214.07,222.25L212.62,222.02L212.85,221.07L211.34,219.64L211.59,217.42L210.75,217.86L206.42,215.09L203.64,215.95L199.86,213.66L197.02,214.37L196.65,213.41L197.99,212.61L197.13,212L195.25,211.98L193.77,213.59L188.52,212.32L185.83,214.4L183.77,214.13L183.44,216.02L182.48,215.69L182.36,213.74L188.38,210.8L184.08,209.65L187.03,207.03L185.52,205.01L185.98,204.25L182.55,203.03L180.31,198.75L180.89,197.91L179.9,194.37L184.51,191.95L183.26,189.19L184.56,185.8L183.23,185.49L183.37,182.6L180.04,179.62L180.57,176.51L178.17,174.76L178.54,170.66L176.88,168.73L177.44,164.39L176.17,158.67L178.11,152.2L179.62,149.66L182.2,148.57L185.21,144.37L185.3,142.89Z",
    map_28: "M210.11,125.28L212.74,121.02L214.25,121.66L219.52,129.75L221.16,129.72L222.8,131.66L225.56,130.38L227.68,130.9L232.35,134.95L233.39,134.29L234.57,135.24L233.78,137.16L234.35,138.92L240.21,141.5L241.15,140.79L245.5,144.2L249.38,145.6L249.54,146.54L248.62,146.77L246.94,146.18L243.99,150.49L241.31,151.47L239.38,154.64L237.12,155.58L237.84,158.63L235.8,161.55L234.74,161.7L236.04,164.18L235.79,166.59L234.85,166.3L235.21,167.78L232.98,168.57L230.15,176.49L228.93,176.18L227.57,173.91L226.5,174.76L226.18,172.97L224.16,173.85L222.02,173.9L221.88,173L219.42,173.7L218.46,171.39L217.27,171.72L214.65,170.24L214.21,168.19L212.41,167.35L212.09,168.42L211.16,167.46L209.92,167.76L209.25,165.77L206.68,164.15L209.12,163.03L209.93,161.61L203.91,159.1L202,155.72L199.36,154.75L193.79,159.68L192.22,158.9L192.09,156.39L189.86,157.31L188.68,156.36L187.67,153.31L188.17,150.89L191.89,145.81L186.43,142.37L185.08,142.49L189.72,139.8L188.18,138.58L189.11,137.27L187.42,135.15L188.47,132.88L189.31,133.34L189.52,132.25L188.32,131.22L189.44,131.1L189.6,129.4L191.41,127.29L193.54,127.3L198.79,124.69L200.11,124.7L201.98,126.39L204.41,125.75L208.03,126.37L209.61,128.41L211.78,128.13L210.11,125.28Z",
    map_29: "M393.02,207.97L395.22,208.77L395.26,211.82L396.13,211.03L396.93,212.48L398.14,212.2L400.02,214.8L404.54,213.64L406.16,214.85L409.51,215.32L409.4,216.61L413.54,216.92L413.92,218.75L413.54,223.35L411.01,225.29L410.72,228L410.22,226.46L408.64,227.83L409.66,229.71L407.96,231.77L405.5,230.27L404.16,230.86L400.51,228.28L399.34,223.16L397.16,222.89L396.91,221.85L396.04,223.78L397.33,224.12L398.48,226.19L395.78,225.29L394.76,226.41L393.81,224.87L391.48,225.75L392.75,223.94L392.05,224.31L390.56,221.63L389.56,221.87L387.02,220.04L386.58,218.42L385.34,221.66L387.97,221.83L388.71,223.93L387.53,223.57L385.68,225.18L385.5,226.63L382.31,228.12L382.25,230.64L380.69,232.81L381.61,235.64L383.95,234.95L387.4,237.88L387.29,239.1L389.21,240.84L391.94,241.7L393.82,240.71L394.66,243.86L397.6,245.19L396.01,247.89L394.69,247L393.96,247.73L387.15,247.18L387.31,250.53L386.31,252.36L385.36,252.44L385.16,253.8L383.86,253.68L383.02,252.07L381.58,252.62L382.18,253.99L379.17,257.87L381.29,260.85L385.29,263.76L392.58,265.64L392.82,268.58L391.96,269.38L393.33,271.58L392.62,273.06L389.7,274.15L389.43,278.46L392.79,281.78L393.79,281.47L392.8,285.28L391.93,285.56L392.37,286.37L397.38,287.08L395.08,290.07L394.99,292.35L396.96,294.62L396.09,296.17L396.71,299.36L394.63,301.38L394.77,302.93L391.76,301.02L394.69,302.97L396.66,307.1L396.03,306.96L395.98,308.32L394.31,307.42L394.5,308.15L393.67,308.2L390.8,307.35L391.27,306.14L390.11,313.52L389.05,313.54L388.73,314.6L388.6,313.41L386.8,313.5L386.01,315.84L384.97,314.42L383.74,315.48L384.16,316.58L382.95,316.39L381.77,312.46L383.04,310.29L382.69,308.29L380.27,307.42L380.86,305.52L379.83,307.21L378.62,306.68L377.97,303.6L376.94,302.89L377.75,306.31L378.73,307.43L381.43,307.92L382.42,309.65L380.07,311.38L378.23,309.56L379.89,311.38L378.19,315.15L373.46,318.8L369.43,319.65L368.65,316.36L365.62,315.59L364.77,312.47L363.39,312.31L362.16,314.36L360.97,314.26L360.73,310.73L357.29,309.64L356.51,308.46L355.12,308.67L355.26,307.23L358.33,305.9L357.23,305.03L357.51,303.59L356.02,303.05L355.67,302.1L356.66,301.82L355.88,300.01L353.94,299.98L353.31,298.02L349.56,295.94L349.95,292.91L351.9,291.97L345.78,291.62L342.52,288.57L340.25,288.85L338.69,287.22L339.9,283.87L339.23,282.39L342.6,281.59L342.53,279.72L344.59,280.11L344.49,281.85L347.41,283L350.1,278.75L356.55,277.62L356.57,274.73L358.33,274.91L358.47,273.75L362.72,275.46L363.51,274.96L362.94,273.99L364.98,274.79L365.84,272.22L364.78,270.58L368.58,271.77L369.88,270.63L369.65,269L370.82,269.66L371.66,268.79L371.19,268.08L373.23,268.32L372.23,266.45L374.1,265.52L375.47,263.15L374.88,259.84L376.18,260.25L377.33,259.38L377.16,256.81L375.63,255.92L378.51,253.3L374.79,249.22L375.07,246.78L375.85,247.11L376.35,245.62L375.14,244.73L374.67,242.68L377.61,240.43L380.28,241.22L379.43,239.48L379.97,237.41L377.12,235.68L377.37,234.07L375.73,233.68L375.38,232.68L376.1,230.19L378.28,229.4L378.47,227.98L380.42,227.44L384.41,224.02L382.37,221.14L383.34,219.98L381.74,220.8L380.89,219.91L382.35,215.04L381.37,211.2L378.78,208.78L379.23,206.59L381.88,208.71L384.71,208.37L387.08,209.31L390.05,207.04L393.02,207.97Z",
    map_30: "M170.55,85.02L163.16,76.67L160.41,77.86L159.68,74.28L156.83,70.26L154.71,70.24L150.97,65.99L145.24,62.93L144.63,59L141.35,57.12L141.8,54.63L140.18,52.62L134.68,53.29L130.87,51.49L127.67,47.42L120.9,47.84L120.75,46.89L117.04,45.57L114.79,44.9L113.59,45.46L113.01,42.62L114.78,40.54L114.6,39.28L111.87,38.35L107.55,38.48L105.06,37.16L102.57,35.37L102.83,32.87L101.46,31.99L98.67,32.81L96.83,31.97L94.24,33.07L91.31,31.36L92.47,29.15L91.28,28.24L92.49,24.38L95.87,23.46L97.81,21.9L97.7,20.88L99.84,20.48L102.07,17.42L101.57,15.61L108.1,14.71L113.01,15.91L116.73,15.47L116.04,13.83L113.49,12.93L112.82,11.33L116.32,11.69L119.55,13.46L121.81,11.5L123.44,11.85L126.56,9.77L129.34,10.6L129.4,8.69L131.32,8.06L132.57,9.33L134.08,9.06L135.58,11.39L143.36,8.45L146.08,13.45L148.64,14.36L151.48,18.16L157.06,20.08L160.51,22.7L162.32,22.45L164.73,25.44L168.74,26.97L170.66,28.72L169.21,30.64L170.72,32.71L173.16,34.05L176.59,33.8L178.54,35.44L181.03,35.39L183.36,39.04L180.36,39.03L181.39,40.21L187.85,40.23L188.08,39.3L192.44,40.61L191.73,39.52L192.31,37.68L195.51,38.99L196.64,36.42L197.7,36.84L198.04,35.3L200,35.09L200.83,34.03L202.65,34.46L205.2,32.64L210.78,31.88L214.15,32.95L215.56,30.13L218.59,29.8L219.47,31.81L225.43,33.72L227.71,34.08L230.14,32.97L232.11,36.09L235.63,37.71L236.26,40.32L233.56,46.48L231.43,55.58L227.56,56.11L226.07,57.28L226.32,60.21L221.01,60.43L222.42,65.18L221.06,65.74L219.36,69.69L214.88,68.53L213.65,69.58L211.83,68.97L209.8,70.46L212.84,74.86L211.94,75.91L213.16,76.25L213.51,77.58L210.05,77.36L210.69,82.44L213.36,84.19L220.01,84.91L218.88,89.39L217.75,89.91L223.21,95.4L219.89,99.53L217.47,98.37L214.01,100.77L213.7,102.52L211.16,103.43L208.34,101.42L207.12,96.21L204.47,98.25L201.51,98.58L199.06,100.75L198.8,98.7L200.52,97.78L200.23,95.02L193.04,98.48L191.49,96.36L191.71,94.89L188.86,92.89L189.39,92.08L187.95,90.74L187.07,90.45L183.16,92.95L182.05,92.43L180.67,93.74L177.62,91.9L177.23,90.55L173.16,89.47L171.04,87L171.52,85.25L170.55,85.02Z",
  };
  return paths[mapId] || "";
}














